// FormenSchreiberDashboard.jsx - Schreiber-Ansicht für Formen-Wertungen
// Japanese Traditional Style (和風トーナメント)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './FormenSchreiberDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Escapes user data before inserting into innerHTML to prevent XSS
const esc = (str) => String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

function FormenSchreiberDashboard({ turnierId: propTurnierId, mode: propMode }) {
  const { t } = useTranslation();
  // TurnierId + KategorieId + Mode aus Props oder URL-Params (für Standalone-Fenster)
  const [searchParams] = useSearchParams();
  const turnierId = propTurnierId || searchParams.get('turnierId');
  const initialKategorieId = searchParams.get('kategorieId');
  const mode = propMode || searchParams.get('mode') || 'formen'; // 'formen' oder 'sv'
  const [kategorien, setKategorien] = useState([]);
  const [selectedKategorie, setSelectedKategorie] = useState(null);
  const [kategorieDetails, setKategorieDetails] = useState(null);
  const [currentTeilnehmer, setCurrentTeilnehmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wertungen, setWertungen] = useState([]);
  const [liveRangliste, setLiveRangliste] = useState([]); // Live aus /rangliste-API

  // Timer State
  const [timerSeconds, setTimerSeconds] = useState(180); // 3 Minuten Standard
  const [timerDuration, setTimerDuration] = useState(180); // konfigurierte Max-Zeit
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStopped, setTimerStopped] = useState(false); // explizit gestoppt (≠ Pause)
  const [timerEnabled, setTimerEnabled] = useState(false); // Timer ist optional, standardmäßig aus
  const [performanceStarted, setPerformanceStarted] = useState(false);

  // Spectator Window
  const [spectatorWindow, setSpectatorWindow] = useState(null);

  // Settings Edit
  const [showSettings, setShowSettings] = useState(false);
  const [editKampfrichter, setEditKampfrichter] = useState(3);
  const [editTimerDuration, setEditTimerDuration] = useState(180);
  const [editTimerEnabled, setEditTimerEnabled] = useState(false);

  // Wertung gespeichert – zeigt "Nächster Wettkämpfer"-Button
  const [wertungGespeichert, setWertungGespeichert] = useState(false);

  // Winner Screen
  const [showWinnerScreen, setShowWinnerScreen] = useState(false);

  // Review Phase
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [reviewRangliste, setReviewRangliste] = useState([]); // editierbare Rangfolge
  const [reviewOriginal, setReviewOriginal] = useState([]);   // Snapshot für Diff
  const [reviewBegruendung, setReviewBegruendung] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  // Disqualifikation
  const [showDisqualifyDialog, setShowDisqualifyDialog] = useState(false);
  const [disqualifyTarget, setDisqualifyTarget] = useState(null);
  const [disqualifyReason, setDisqualifyReason] = useState('');

  // Punktgleichheit / Tie-Breaker
  const [showTieBreakerDialog, setShowTieBreakerDialog] = useState(false);
  const [tieBreakerData, setTieBreakerData] = useState(null);

  // Kategorie-Filter (oben im Header)
  const [katFilter, setKatFilter] = useState('offen'); // 'alle' | 'offen' | 'abgeschlossen'
  const [allKategorien, setAllKategorien] = useState([]);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [autoGenerateMsg, setAutoGenerateMsg] = useState('');

  // Filter für Startreihenfolge
  const [filterStatus, setFilterStatus] = useState('alle'); // 'alle' | 'offen' | 'abgeschlossen'

  // Wettkämpfer hinzufügen Modal
  const [showAddFighter, setShowAddFighter] = useState(false);
  const [addFighterSearch, setAddFighterSearch] = useState('');
  const [addFighterAll, setAddFighterAll] = useState([]);
  const [addFighterSelected, setAddFighterSelected] = useState([]);
  const [addFighterLoading, setAddFighterLoading] = useState(false);
  const [addFighterSaving, setAddFighterSaving] = useState(false);

  const token = localStorage.getItem('token');
  const timerRef = useRef(null);
  const saveDebounceRef = useRef({});

  // Timer Logic
  useEffect(() => {
    if (timerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => prev - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setTimerRunning(false);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, timerSeconds]);

  // Ref für erste Kategorie-Auswahl
  const initialKategorieSelectedRef = useRef(false);

  // Kategorien laden (je nach mode: Formen oder SV)
  const fetchKategorien = useCallback(async () => {
    if (!turnierId) return;

    try {
      const endpoint = mode === 'sv'
        ? `${API_URL}/api/formen/sv/${turnierId}`
        : `${API_URL}/api/formen/turnier/${turnierId}`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        // Alle Kategorien mit Teilnehmern speichern
        const kategorienMitTeilnehmern = data.data.filter(k => parseInt(k.teilnehmer_anzahl) > 0);
        setAllKategorien(kategorienMitTeilnehmern);

        // Aktive Kategorien filtern
        const activeKategorien = kategorienMitTeilnehmern.filter(k => k.status !== 'completed');
        setKategorien(activeKategorien);

        // Vorausgewählte Kategorie aus URL-Param bevorzugen, sonst erste aktive
        if (initialKategorieId) {
          const match = kategorienMitTeilnehmern.find(k => String(k.kategorie_id) === String(initialKategorieId));
          return match ? match.kategorie_id : (activeKategorien.length > 0 ? activeKategorien[0].kategorie_id : null);
        }
        return activeKategorien.length > 0 ? activeKategorien[0].kategorie_id : null;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    } finally {
      setLoading(false);
    }
    return null;
  }, [turnierId, token, mode, initialKategorieId]);

  // Formen-Kategorien automatisch aus Anmeldungen generieren
  const handleAutoGenerate = async () => {
    setAutoGenerating(true);
    setAutoGenerateMsg('');
    try {
      const response = await fetch(`${API_URL}/api/formen/auto-generate/${turnierId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAutoGenerateMsg(data.message);
        const firstId = await fetchKategorien();
        if (firstId) {
          initialKategorieSelectedRef.current = true;
          handleKategorieSelect(firstId);
        }
      } else {
        setAutoGenerateMsg(data.error || 'Fehler beim Generieren');
      }
    } catch (e) {
      setAutoGenerateMsg('Netzwerkfehler');
    } finally {
      setAutoGenerating(false);
    }
  };

  // Initiale Kategorie laden
  useEffect(() => {
    const loadInitial = async () => {
      const firstKategorieId = await fetchKategorien();
      if (firstKategorieId && !initialKategorieSelectedRef.current) {
        initialKategorieSelectedRef.current = true;
        handleKategorieSelect(firstKategorieId);
      }
    };
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKategorien]);

  // Kategorie-Details laden
  const handleKategorieSelect = async (kategorieId) => {
    try {
      const response = await fetch(`${API_URL}/api/formen/kategorie/${kategorieId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setKategorieDetails(data.data);
        setSelectedKategorie(data.data.kategorie);
        setLiveRangliste([]); // reset — fetchLiveRangliste füllt neu

        const nextTeilnehmer = data.data.teilnehmer
          .sort((a, b) => (a.startnummer || 999) - (b.startnummer || 999))
          .find(tn => tn.status !== 'completed' && tn.status !== 'disqualified');

        if (nextTeilnehmer) {
          selectTeilnehmer(nextTeilnehmer, kategorieId, data.data.kategorie?.anzahl_kampfrichter); // anzahl_kampfrichter direkt übergeben (State noch stale)
        } else {
          setCurrentTeilnehmer(null);
          // Offene Punktgleichheit hat Vorrang — sie muss entschieden sein,
          // bevor über die Rangfolge gesprochen wird. Sonst bliebe der
          // Tie-Breaker im Review-Status unerreichbar.
          const offenerGleichstand = checkForMoreTiesWithData(data.data);

          // Wenn Kategorie im Review-Status: Review-Screen anzeigen
          if (!offenerGleichstand && data.data.kategorie?.status === 'review') {
            const sorted = [...data.data.teilnehmer]
              .filter(tn => tn.status === 'completed')
              .sort((a, b) => {
              // Primär nach vergebener Platzierung: bei entschiedener Punktgleichheit
              // steht dort das Ergebnis des Handentscheids, das die reine
              // Punktsortierung sonst wieder überschreiben würde.
              if (a.platzierung && b.platzierung && a.platzierung !== b.platzierung) {
                return a.platzierung - b.platzierung;
              }
              return (parseFloat(b.endwertung) || 0) - (parseFloat(a.endwertung) || 0);
            })
              .map((tn, idx) => ({ ...tn, platzierung: tn.platzierung || idx + 1 }));
            setReviewRangliste(sorted);
            setReviewOriginal(sorted.map(tn => ({ wettkaempfer_id: tn.wettkaempfer_id, platzierung: tn.platzierung })));
            setReviewBegruendung('');
            setShowReviewScreen(true);
          }
        }

        // Live-Rangliste direkt laden (athleteOverride = nextTeilnehmer, da State noch nicht updated)
        fetchLiveRangliste(kategorieId, nextTeilnehmer || null);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorie:', error);
    }
  };

  // Hilfsfunktion: Prüft ob tie_resolved falsch ist (behandelt 0, false, null, undefined)
  const isNotResolved = (tieResolved) => {
    return !tieResolved || tieResolved === 0 || tieResolved === '0';
  };

  // Direkte Prüfung auf Gleichstände mit geladenen Daten
  // Gibt true zurück wenn ein Gleichstand gefunden wurde
  const checkForMoreTiesWithData = (kategorieData) => {
    if (!kategorieData?.teilnehmer) return false;

    const completed = kategorieData.teilnehmer
      .filter(tn => tn.status === 'completed')
      .map(tn => ({
        ...tn,
        // Punkte auf 2 Dezimalstellen normalisieren
        gesamt_punkte_normalized: Math.round((parseFloat(tn.gesamt_punkte) || 0) * 100) / 100
      }))
      .sort((a, b) => b.gesamt_punkte_normalized - a.gesamt_punkte_normalized);

    for (let i = 0; i < completed.length - 1; i++) {
      const current = completed[i];
      const next = completed[i + 1];

      if (current.gesamt_punkte_normalized === next.gesamt_punkte_normalized &&
          isNotResolved(current.tie_resolved) && isNotResolved(next.tie_resolved)) {

        // Alle mit gleicher Punktzahl sammeln
        const tied = completed.filter(tn =>
          tn.gesamt_punkte_normalized === current.gesamt_punkte_normalized && isNotResolved(tn.tie_resolved)
        );

        if (tied.length > 1) {
          setTieBreakerData({
            teilnehmer: tied,
            punkte: current.gesamt_punkte_normalized,
            platzierung: i + 1
          });
          setShowTieBreakerDialog(true);
          return true; // Gleichstand gefunden
        }
      }
    }
    return false; // Kein Gleichstand
  };

  // Teilnehmer auswählen (katId: optional direkter Parameter, wenn selectedKategorie noch null ist)
  const selectTeilnehmer = async (teilnehmer, katId, kampfrichterOverride) => {
    setCurrentTeilnehmer(teilnehmer);
    setPerformanceStarted(false);
    setTimerRunning(false);
    setTimerStopped(false);
    setTimerSeconds(timerDuration);
    setWertungGespeichert(false);

    const effectiveKatId = katId || selectedKategorie?.kategorie_id;
    const anzahlKampfrichter = kampfrichterOverride ?? selectedKategorie?.anzahl_kampfrichter ?? 3;

    if (!effectiveKatId) return; // noch keine Kategorie geladen

    // Versuche gespeicherte Wertungen zu laden
    const emptyWertungen = Array.from({ length: anzahlKampfrichter }, (_, i) => ({
      kampfrichter_nummer: i + 1, punkte: '', notiz: ''
    }));
    let spectatorWertungen = emptyWertungen;
    try {
      const response = await fetch(
        `${API_URL}/api/formen/wertung/${effectiveKatId}/${teilnehmer.wettkaempfer_id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();

      if (data.success && data.data?.wertungen && data.data.wertungen.length > 0) {
        // Gespeicherte Wertungen vorhanden - laden
        const loadedWertungen = Array.from({ length: anzahlKampfrichter }, (_, i) => {
          const saved = data.data.wertungen.find(w => w.kampfrichter_nummer === i + 1);
          return {
            kampfrichter_nummer: i + 1,
            punkte: saved ? saved.punkte.toString() : '',
            notiz: saved?.notiz || ''
          };
        });
        setWertungen(loadedWertungen);
        spectatorWertungen = loadedWertungen;
      } else {
        // Keine gespeicherten Wertungen - leere initialisieren
        setWertungen(emptyWertungen);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Wertungen:', error);
      setWertungen(emptyWertungen);
    }

    // Neuen Athleten sofort auf Spectator zeigen (mit korrekten Wertungen)
    renderSpectator(teilnehmer, spectatorWertungen, liveRangliste);
  };

  // Timer formatieren
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start / Pause Performance
  const handleTogglePerformance = () => {
    if (timerStopped) return; // nach Stop kein Resume
    if (!performanceStarted) {
      setPerformanceStarted(true);
      setTimerRunning(true);
    } else {
      setTimerRunning(!timerRunning);
    }
    updateSpectatorWindow();
  };

  // Stop — beendet den Lauf, Zeit einfrieren (kein Resume möglich)
  const handleStopTimer = () => {
    setTimerRunning(false);
    setTimerStopped(true);
    updateSpectatorWindow();
  };

  // Reset — alles zurücksetzen
  const handleResetTimer = () => {
    setTimerRunning(false);
    setTimerStopped(false);
    setTimerSeconds(timerDuration);
    setPerformanceStarted(false);
  };

  // ↩ Wertung eines Teilnehmers zurücksetzen (Undo)
  const resetTeilnehmerWertung = async (tn, e) => {
    e.stopPropagation();
    if (!window.confirm(`Wertung von ${tn.vorname} ${tn.nachname} wirklich zurücksetzen?\n\nAlle Punkte werden gelöscht und der Teilnehmer kann neu bewertet werden.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/formen/wertung/${tn.kategorie_id || selectedKategorie?.kategorie_id}/${tn.wettkaempfer_id}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const d = await r.json();
      if (d.success) {
        // Eingabefelder leeren wenn dieser Athlet gerade aktiv ist
        if (currentTeilnehmer?.wettkaempfer_id === tn.wettkaempfer_id) {
          setWertungen(prev => prev.map(w => ({ ...w, punkte: '' })));
          setWertungGespeichert(false);
        }
        // Kategorie + Rangliste komplett neu laden
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        await fetchKategorien();
        fetchLiveRangliste(selectedKategorie.kategorie_id);
      } else {
        alert('Fehler: ' + (d.error || 'Unbekannter Fehler'));
      }
    } catch {
      alert(t('admin.formen_schreiber.network_error_reset'));
    }
  };

  // Live-Rangliste aus API holen und Spectator updaten
  const fetchLiveRangliste = async (kategorieId, athleteOverride) => {
    if (!kategorieId) return;
    try {
      const res  = await fetch(`${API_URL}/api/formen/rangliste/${kategorieId}`);
      const data = await res.json();
      if (data.success) {
        // MySQL DECIMAL kommt als String → normalisieren
        const normalized = data.data.map(tn => ({
          ...tn,
          gesamt_punkte: parseFloat(tn.gesamt_punkte) || 0,
          endwertung:    parseFloat(tn.endwertung)    || 0,
        }));
        setLiveRangliste(normalized);
        // Spectator mit aktueller Rangliste neu rendern
        // athleteOverride: nötig wenn Athletenwechsel noch nicht in State gespiegelt
        const athlete = athleteOverride !== undefined ? athleteOverride : currentTeilnehmer;
        renderSpectator(athlete, wertungen, normalized, { saved: wertungGespeichert, naechster: wertungGespeichert ? naechsterTeilnehmer : null });
      }
    } catch { /* silent */ }
  };

  // Auto-Save einzelne Wertung (debounced) + danach Rangliste live refreshen
  const autoSaveWertung = (kampfrichterNummer, punkte) => {
    if (!currentTeilnehmer || !selectedKategorie) return;

    const punkteMin = parseFloat(selectedKategorie.punkte_min) || 6.0;
    const punkteMax = parseFloat(selectedKategorie.punkte_max) || 10.0;
    const punkteValue = parseFloat(punkte);

    if (isNaN(punkteValue) || punkteValue < punkteMin || punkteValue > punkteMax) return;

    const key = `${currentTeilnehmer.wettkaempfer_id}-${kampfrichterNummer}`;
    if (saveDebounceRef.current[key]) clearTimeout(saveDebounceRef.current[key]);

    saveDebounceRef.current[key] = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/formen/wertung`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kategorie_id: selectedKategorie.kategorie_id,
            wettkaempfer_id: currentTeilnehmer.wettkaempfer_id,
            wertungen: [{ kampfrichter_nummer: kampfrichterNummer, punkte: punkteValue, notiz: '' }],
            partial: true
          })
        });
        // Nach jedem Partial-Save: Rangliste live aktualisieren
        fetchLiveRangliste(selectedKategorie.kategorie_id);
      } catch { /* silent */ }
      delete saveDebounceRef.current[key];
    }, 300);
  };

  // Prüfen ob alle Wertungen komplett und automatisch abschließen
  const checkAndAutoComplete = async (currentWertungen) => {
    if (!currentTeilnehmer || !selectedKategorie) return;

    const punkteMin = parseFloat(selectedKategorie.punkte_min) || 6.0;
    const punkteMax = parseFloat(selectedKategorie.punkte_max) || 10.0;

    // Prüfen ob alle Wertungen ausgefüllt und gültig sind
    const alleFilled = currentWertungen.every(w => w.punkte !== '' && !isNaN(parseFloat(w.punkte)));
    if (!alleFilled) return;

    const alleValid = currentWertungen.every(w => {
      const p = parseFloat(w.punkte);
      return p >= punkteMin && p <= punkteMax;
    });
    if (!alleValid) return;

    // Alle Wertungen komplett - finale Speicherung
    const gesamtPunkteCalc = currentWertungen.reduce((sum, w) => sum + parseFloat(w.punkte), 0);

    try {
      const formattedWertungen = currentWertungen.map(w => ({
        ...w,
        punkte: parseFloat(w.punkte)
      }));

      const response = await fetch(`${API_URL}/api/formen/wertung`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kategorie_id: selectedKategorie.kategorie_id,
          wettkaempfer_id: currentTeilnehmer.wettkaempfer_id,
          wertungen: formattedWertungen
        })
      });

      const data = await response.json();
      if (data.success) {
        // Rangliste live updaten (lädt neue Platzierungen)
        fetchLiveRangliste(selectedKategorie.kategorie_id);
        // Spectator sofort mit saved=true + nächstem Athleten aktualisieren
        renderSpectator(currentTeilnehmer, currentWertungen, liveRangliste, { saved: true, naechster: naechsterTeilnehmer });
        // Kategorien-Liste refreshen (Status-Badge oben)
        fetchKategorien();
        // Anzeigen: Wertung gespeichert → "Nächster Wettkämpfer"-Button erscheint
        setWertungGespeichert(true);
        // Kategorie auf Gleichstände prüfen (ohne Auto-Advance)
        setTimeout(() => checkCategoryComplete(), 300);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
    }
  };

  // Manuell zum nächsten Wettkämpfer wechseln
  const handleNaechsterTeilnehmer = async () => {
    if (!selectedKategorie) return;
    handleResetTimer();
    await handleKategorieSelect(selectedKategorie.kategorie_id);
    await fetchKategorien();
  };

  // Punkte ändern mit Auto-Save
  const handlePunkteChange = (index, value) => {
    const newWertungen = [...wertungen];
    newWertungen[index].punkte = value;
    setWertungen(newWertungen);
    setWertungGespeichert(false); // Überschreiben: Status zurücksetzen

    if (value !== '' && !isNaN(parseFloat(value))) {
      autoSaveWertung(index + 1, value);
      updateSpectatorWithScores(newWertungen);
      checkAndAutoComplete(newWertungen);
    }
  };

  // Quick Score mit Auto-Save
  const handleQuickScore = (index, points) => {
    const newWertungen = [...wertungen];
    newWertungen[index].punkte = points.toString();
    setWertungen(newWertungen);
    setWertungGespeichert(false); // Überschreiben: Status zurücksetzen

    autoSaveWertung(index + 1, points);
    updateSpectatorWithScores(newWertungen);
    checkAndAutoComplete(newWertungen);
  };

  // Zentrale Spectator-Render-Funktion — wird von allen Stellen aufgerufen
  const renderSpectator = (teilnehmer, currentWertungen, ranglisteData, opts = {}) => {
    if (!spectatorWindow || spectatorWindow.closed || !teilnehmer) return;
    const { saved = false, naechster = null } = opts;

    const filledScores = currentWertungen.filter(w => w.punkte !== '' && !isNaN(parseFloat(w.punkte)));
    const summe = filledScores.reduce((sum, w) => sum + parseFloat(w.punkte), 0);
    const alleGefuellt = filledScores.length === currentWertungen.length && currentWertungen.length > 0;

    // Platzierung des aktuellen Athleten in der Rangliste
    const currentInRangliste = (ranglisteData || []).find(r => r.wettkaempfer_id === teilnehmer.wettkaempfer_id);
    const platzierung = currentInRangliste?.platzierung || null;
    const rankLabel = platzierung === 1 ? '🥇 Platz 1' : platzierung === 2 ? '🥈 Platz 2' : platzierung === 3 ? '🥉 Platz 3' : platzierung ? `Platz ${platzierung}` : null;

    const scoresHTML = currentWertungen.map((w, idx) => `
      <div class="score-item ${w.punkte ? 'filled' : ''}">
        <div class="score-label">KR ${idx + 1}</div>
        <div class="score-value">${w.punkte || '—'}</div>
      </div>
    `).join('');

    const ranglisteHTML = (ranglisteData || []).map((tn, i) => {
      const pts   = parseFloat(tn.gesamt_punkte) || 0;
      const isCur = tn.wettkaempfer_id === teilnehmer.wettkaempfer_id;
      const isDone = tn.status === 'completed';
      const medal  = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
      return `<div style="display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;margin-bottom:3px;
                          background:${isCur ? 'rgba(212,175,55,0.18)' : 'transparent'};
                          border:${isCur ? '1px solid rgba(212,175,55,0.5)' : '1px solid transparent'}">
        <span style="font-size:0.95rem;min-width:26px;text-align:center">${medal}</span>
        <span style="flex:1;font-size:0.88rem;color:${isCur ? '#ffd700' : '#ddd'};font-weight:${isCur ? '700' : '400'};
                     white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
          ${esc(tn.vorname)} ${esc(tn.nachname)}
        </span>
        <span style="font-size:0.95rem;font-weight:700;color:${isDone ? '#5dca7e' : '#e6b400'};min-width:42px;text-align:right">
          ${pts > 0 ? pts.toFixed(2) : '—'}
        </span>
      </div>`;
    }).join('');

    spectatorWindow.document.body.innerHTML = `
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body {
          background: linear-gradient(160deg,#0d0a07 0%,#1a1510 60%,#0d0a07 100%);
          color:#fff; font-family:'Segoe UI',sans-serif;
          min-height:100vh; display:flex; overflow:hidden;
        }
        @keyframes scoreIn { from{opacity:0;transform:scale(0.6)} to{opacity:1;transform:scale(1)} }
        @keyframes pulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.025)} }
        @keyframes fadein  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }

        /* ── Layout ── */
        .main   { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:5rem 2rem 2rem; }
        .sidebar{ width:300px; min-width:260px; background:rgba(0,0,0,0.5);
                  border-left:1px solid rgba(212,175,55,0.2);
                  display:flex; flex-direction:column; padding:5rem 1rem 1rem; overflow-y:auto; }

        /* ── Header ── */
        .header-bar {
          position:absolute; top:0; left:0; right:0;
          background:linear-gradient(180deg,#8B0000,#5c0000);
          padding:0.85rem 2rem; border-bottom:3px solid #d4af37;
          display:flex; justify-content:space-between; align-items:center; z-index:10;
        }
        .hdr-kat   { color:#d4af37; font-size:1rem; font-weight:600; }
        .hdr-title { color:#fff; font-size:1.3rem; font-weight:800; letter-spacing:3px; }
        .hdr-live  { color:#4CAF50; font-size:0.9rem; letter-spacing:2px; font-weight:700; }

        /* ── Athlete ── */
        .athlete-num  { display:inline-block; background:linear-gradient(135deg,#d4af37,#b8942d);
                        color:#1a1510; padding:0.4rem 1.8rem; border-radius:30px;
                        font-size:1.3rem; font-weight:900; margin-bottom:0.75rem; }
        .athlete-name { font-size:3.2rem; font-weight:900; line-height:1.1; margin-bottom:0.4rem;
                        text-shadow:0 2px 12px rgba(0,0,0,0.5); }
        .athlete-sub  { color:#aaa; font-size:1.1rem; display:flex; gap:1rem; justify-content:center; flex-wrap:wrap; }
        .athlete-belt { background:rgba(255,255,255,0.10); padding:2px 12px; border-radius:20px; font-size:0.95rem; }

        /* ── Scores ── */
        .scores-row   { display:flex; gap:1.2rem; margin:1.75rem 0; flex-wrap:wrap; justify-content:center; }
        .score-item   { background:rgba(0,0,0,0.45); border:2px solid #3a2a1a;
                        border-radius:12px; padding:1.2rem 1.6rem; text-align:center; min-width:110px; }
        .score-item.filled { border-color:#d4af37; background:rgba(212,175,55,0.12);
                             animation:scoreIn 0.25s ease-out; }
        .score-lbl   { color:#777; font-size:0.8rem; letter-spacing:2px; margin-bottom:0.4rem; }
        .score-val   { font-size:2.3rem; font-weight:700; color:#d4af37; }
        .score-item:not(.filled) .score-val { color:#444; }

        /* ── Summe ── */
        .summe-box  { background:linear-gradient(180deg,rgba(139,0,0,0.45),rgba(92,0,0,0.2));
                      border:3px solid #d4af37; border-radius:14px; padding:1.5rem 3.5rem;
                      text-align:center; animation:${saved ? 'none' : 'pulse 2s ease-in-out infinite'}; }
        .summe-lbl  { color:#d4af37; font-size:1.2rem; letter-spacing:4px; margin-bottom:0.3rem; }
        .summe-val  { font-size:5.5rem; font-weight:900; line-height:1; color:#fff; }
        .summe-sub  { color:#888; font-size:0.9rem; margin-top:0.4rem; }

        /* ── Platzierung (nach Save) ── */
        .rank-badge { margin-top:1.2rem; font-size:2rem; font-weight:900;
                      animation:fadein 0.4s ease-out;
                      color:${platzierung <= 3 ? '#d4af37' : '#aaa'}; text-align:center; }

        /* ── Nächster ── */
        .next-box { margin-top:1.5rem; padding:0.9rem 1.5rem; border-radius:10px;
                    background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.12);
                    text-align:center; animation:fadein 0.5s ease-out; }
        .next-lbl { color:#888; font-size:0.8rem; letter-spacing:2px; margin-bottom:0.25rem; }
        .next-name{ color:#fff; font-size:1.2rem; font-weight:700; }

        /* ── Sidebar Rangliste ── */
        .sidebar-title { color:#d4af37; font-size:0.8rem; letter-spacing:3px;
                         margin-bottom:0.75rem; text-transform:uppercase; padding:0 8px; }
      </style>

      <div class="header-bar">
        <div class="hdr-kat">${selectedKategorie?.kategorie_name || ''}</div>
        <div class="hdr-title">点数 WERTUNG</div>
        <div class="hdr-live">${saved ? '✅ GESPEICHERT' : 'LIVE'}</div>
      </div>

      <div class="main">
        <div class="athlete-num">#${teilnehmer.startnummer}</div>
        <div class="athlete-name">${esc(teilnehmer.vorname)} ${esc(teilnehmer.nachname)}</div>
        <div class="athlete-sub">
          <span>${esc(teilnehmer.verein_name || '')}</span>
          ${teilnehmer.gurtfarbe ? `<span class="athlete-belt">${esc(teilnehmer.gurtfarbe)}</span>` : ''}
        </div>

        <div class="scores-row">${scoresHTML}</div>

        <div class="summe-box">
          <div class="summe-lbl">合計 GESAMT</div>
          <div class="summe-val">${summe > 0 || alleGefuellt ? summe.toFixed(2) : '—'}</div>
          <div class="summe-sub">${filledScores.length} von ${currentWertungen.length} Kampfrichter</div>
        </div>

        ${saved && rankLabel ? `<div class="rank-badge">${rankLabel}</div>` : ''}

        ${saved && naechster ? `
          <div class="next-box">
            <div class="next-lbl">NÄCHSTER WETTKÄMPFER</div>
            <div class="next-name">#${naechster.startnummer} ${esc(naechster.vorname)} ${esc(naechster.nachname)}</div>
          </div>` : ''}
      </div>

      <div class="sidebar">
        <div class="sidebar-title">順位 Aktuelle Rangliste</div>
        <div id="spectator-rangliste">${ranglisteHTML}</div>
      </div>
    `;
  };

  // Compat-Wrapper für bestehende Aufrufe
  const updateSpectatorWithScores = (currentWertungen) => {
    renderSpectator(currentTeilnehmer, currentWertungen, liveRangliste);
  };

  // Disqualifizieren
  const handleDisqualify = async () => {
    if (!disqualifyTarget || !disqualifyReason.trim()) {
      alert(t('admin.formen_schreiber.please_enter_reason'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/formen/teilnehmer/${disqualifyTarget.wettkaempfer_id}/disqualify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kategorie_id: selectedKategorie.kategorie_id,
          grund: disqualifyReason
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowDisqualifyDialog(false);
        setDisqualifyTarget(null);
        setDisqualifyReason('');
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        await fetchKategorien();
        checkCategoryComplete();
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler bei Disqualifikation:', error);
    }
  };

  // Punktgleichheit lösen (Tie-Breaker)
  const handleTieBreaker = async (method, winnerId) => {
    if (!tieBreakerData) return;

    try {
      const response = await fetch(`${API_URL}/api/formen/tie-breaker`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kategorie_id: selectedKategorie.kategorie_id,
          teilnehmer_ids: tieBreakerData.teilnehmer.map(tn => tn.wettkaempfer_id),
          method: method, // 'handwertung' oder 'extrapunkt'
          winner_id: winnerId,
          platzierung: tieBreakerData.platzierung
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowTieBreakerDialog(false);
        setTieBreakerData(null);
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        checkForMoreTies();
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler bei Tie-Breaker:', error);
    }
  };

  // Review-Phase: Rang-Element nach oben/unten verschieben
  const handleReviewMove = (index, direction) => {
    const newList = [...reviewRangliste];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    // Platzierungen neu vergeben
    newList.forEach((tn, i) => { tn.platzierung = i + 1; });
    setReviewRangliste(newList);
  };

  // Prüfen ob sich etwas geändert hat (Original vs. aktuell)
  const reviewHasChanges = () => {
    return reviewRangliste.some((tn, idx) => {
      const orig = reviewOriginal.find(o => o.wettkaempfer_id === tn.wettkaempfer_id);
      return orig && orig.platzierung !== tn.platzierung;
    });
  };

  // Review-Phase bestätigen → Backend
  const handleFinalize = async () => {
    if (!selectedKategorie) return;
    const hasChanges = reviewHasChanges();
    if (hasChanges && (!reviewBegruendung || reviewBegruendung.trim().length < 5)) {
      alert('Bitte eine Begründung für die Änderungen angeben (mind. 5 Zeichen).');
      return;
    }
    setReviewSaving(true);
    try {
      const korrekturen = hasChanges
        ? reviewRangliste
            .filter(tn => {
              const orig = reviewOriginal.find(o => o.wettkaempfer_id === tn.wettkaempfer_id);
              return orig && orig.platzierung !== tn.platzierung;
            })
            .map(tn => {
              const orig = reviewOriginal.find(o => o.wettkaempfer_id === tn.wettkaempfer_id);
              return {
                wettkaempfer_id: tn.wettkaempfer_id,
                name: `${tn.vorname} ${tn.nachname}`,
                platzierung_vorher: orig.platzierung,
                platzierung_nachher: tn.platzierung
              };
            })
        : [];

      const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}/finalize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ korrekturen, begruendung: reviewBegruendung })
      });
      const data = await response.json();
      if (data.success) {
        setShowReviewScreen(false);
        await fetchKategorien();
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        setShowWinnerScreen(true);
        setTimeout(() => showWinnerOnSpectator(), 500);
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler bei Finalisierung:', error);
      alert('Verbindungsfehler beim Bestätigen.');
    } finally {
      setReviewSaving(false);
    }
  };

  // Prüfen ob Kategorie abgeschlossen
  const checkCategoryComplete = async () => {
    if (!selectedKategorie?.kategorie_id) return;

    // Frisch laden statt kategorieDetails zu nutzen: der State-Snapshot hinkt
    // nach dem Speichern eine Wertung hinterher, wodurch der zuletzt gewertete
    // Teilnehmer noch als 'pending' zählte und die Prüfung nie ansprang.
    const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (!data.success) return;

    const pending = data.data.teilnehmer.filter(tn =>
      tn.status !== 'completed' && tn.status !== 'disqualified'
    );

    if (pending.length === 0) {
      // Kategorie fertig - auf Gleichstände prüfen
      checkForMoreTies(data.data);
    }
  };

  // Auf weitere Gleichstände prüfen
  // kategorieData: bereits geladene Daten (spart einen zweiten Request)
  const checkForMoreTies = async (kategorieData) => {
    if (!selectedKategorie?.kategorie_id) return;

    let data = kategorieData ? { success: true, data: kategorieData } : null;

    if (!data) {
      // Neu laden um aktuelle Daten zu haben
      const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      data = await response.json();
    }

    if (!data.success) return;

    // checkForMoreTiesWithData verwenden - gibt true zurück wenn Gleichstand gefunden
    const tieFound = checkForMoreTiesWithData(data.data);

    // Wenn kein Gleichstand gefunden, prüfen ob alle fertig
    if (!tieFound) {
      const allDone = data.data.teilnehmer.every(tn =>
        tn.status === 'completed' || tn.status === 'disqualified'
      );

      const completed = data.data.teilnehmer.filter(tn => tn.status === 'completed');

      if (allDone && completed.length > 0) {
        // Review-Phase öffnen (Kampfrichter prüfen Rangfolge vor Abschluss)
        const sorted = [...data.data.teilnehmer]
          .filter(tn => tn.status === 'completed')
          .sort((a, b) => {
              // Primär nach vergebener Platzierung: bei entschiedener Punktgleichheit
              // steht dort das Ergebnis des Handentscheids, das die reine
              // Punktsortierung sonst wieder überschreiben würde.
              if (a.platzierung && b.platzierung && a.platzierung !== b.platzierung) {
                return a.platzierung - b.platzierung;
              }
              return (parseFloat(b.endwertung) || 0) - (parseFloat(a.endwertung) || 0);
            })
          .map((tn, idx) => ({ ...tn, platzierung: tn.platzierung || idx + 1 }));
        setReviewRangliste(sorted);
        setReviewOriginal(sorted.map(tn => ({ wettkaempfer_id: tn.wettkaempfer_id, platzierung: tn.platzierung })));
        setReviewBegruendung('');
        setShowReviewScreen(true);
      }
    }
  };

  // Wertung speichern
  // Kampfrichter-Anzahl ändern
  const handleUpdateKampfrichter = async (anzahl) => {
    if (!selectedKategorie) return;

    try {
      const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ anzahl_kampfrichter: anzahl })
      });
      const data = await response.json();
      if (data.success) {
        // Reload category
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        setShowSettings(false);
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
    }
  };

  // Startreihenfolge mischen
  // Prüfen ob Kategorie bereits gestartet (mindestens ein Teilnehmer bewertet)
  const kategorieGestartet = kategorieDetails?.teilnehmer?.some(
    tn => tn.status === 'completed' || tn.gesamt_punkte > 0
  ) || false;

  const handleShuffle = async () => {
    if (!selectedKategorie) return;

    // Nicht mischen wenn bereits gestartet
    if (kategorieGestartet) {
      alert(t('admin.formen_schreiber.shuffle_not_possible'));
      return;
    }

    if (!window.confirm('Startreihenfolge zufällig mischen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}/startreihenfolge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode: 'shuffle' })
      });
      const data = await response.json();
      if (data.success) {
        await handleKategorieSelect(selectedKategorie.kategorie_id);
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler beim Mischen:', error);
    }
  };

  // Wettkämpfer aus Kategorie entfernen
  const handleRemoveTeilnehmer = async (tn, e) => {
    e.stopPropagation();
    const hasScore = tn.status === 'completed' || tn.status === 'disqualified' || parseFloat(tn.gesamt_punkte) > 0;
    const msg = hasScore
      ? `${tn.vorname} ${tn.nachname} hat bereits eine Wertung. Wirklich entfernen?`
      : `${tn.vorname} ${tn.nachname} aus dieser Kategorie entfernen?`;
    if (!window.confirm(msg)) return;
    try {
      const katId = tn.kategorie_id || selectedKategorie?.kategorie_id;
      const res = await fetch(`${API_URL}/api/formen/kategorie/${katId}/teilnehmer/${tn.wettkaempfer_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        if (currentTeilnehmer?.wettkaempfer_id === tn.wettkaempfer_id) {
          await handleKategorieSelect(selectedKategorie.kategorie_id);
        } else {
          await handleKategorieSelect(selectedKategorie.kategorie_id);
        }
        await fetchKategorien();
      } else {
        alert('Fehler: ' + (data.message || 'Unbekannt'));
      }
    } catch { alert('Netzwerkfehler'); }
  };

  // Wettkämpfer-Suche: alle laden (einmalig beim Öffnen)
  const handleOpenAddFighter = async () => {
    setAddFighterSearch('');
    setAddFighterSelected([]);
    setShowAddFighter(true);
    setAddFighterLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wettkaempfer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAddFighterAll(data.data || []);
    } catch { /* silent */ }
    setAddFighterLoading(false);
  };

  const handleAddFighters = async () => {
    if (!addFighterSelected.length || !selectedKategorie) return;
    setAddFighterSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}/teilnehmer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ wettkaempfer_ids: addFighterSelected })
      });
      const data = await res.json();
      if (data.success) {
        setShowAddFighter(false);
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        await fetchKategorien();
      } else {
        alert('Fehler: ' + (data.message || 'Unbekannt'));
      }
    } catch { alert('Netzwerkfehler'); }
    setAddFighterSaving(false);
  };

  // Zurück zur Übersicht
  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else {
      window.history.back();
    }
  };

  // Reset Kategorie
  const handleResetKategorie = async () => {
    if (!selectedKategorie) return;

    if (!window.confirm(`Wirklich alle Wertungen für "${selectedKategorie.kategorie_name}" zurücksetzen?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/formen/kategorie/${selectedKategorie.kategorie_id}/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm: 'RESET' })
      });
      const data = await response.json();
      if (data.success) {
        alert('Kategorie wurde zurückgesetzt!');
        await handleKategorieSelect(selectedKategorie.kategorie_id);
        await fetchKategorien();
      } else {
        alert('Fehler: ' + data.message);
      }
    } catch (error) {
      console.error('Fehler beim Reset:', error);
      alert(t('admin.formen_schreiber.error_reset_category'));
    }
  };

  // Rangliste auf Spectator-Fenster pushen (Live-Updates)
  const updateSpectatorRangliste = (ranglisteData) => {
    if (!spectatorWindow || spectatorWindow.closed || !ranglisteData?.length) return;
    // Nur updaten wenn der Spectator gerade die Score-Ansicht zeigt (nicht die Siegerehrung)
    const rankEl = spectatorWindow.document.getElementById('spectator-rangliste');
    if (!rankEl) return;
    rankEl.innerHTML = ranglisteData.map((tn, i) => {
      const pts = parseFloat(tn.gesamt_punkte) || 0;
      const isComplete = tn.status === 'completed';
      return `<div style="display:flex;align-items:center;gap:12px;padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.08)">
        <span style="font-size:1.2rem;font-weight:900;color:#d4af37;min-width:28px">${i + 1}.</span>
        <span style="flex:1;font-size:1rem;color:#fff">${esc(tn.vorname)} ${esc(tn.nachname)}</span>
        <span style="font-size:1.1rem;font-weight:700;color:${isComplete ? '#5dca7e' : '#e6b400'}">${pts.toFixed(2)}</span>
      </div>`;
    }).join('');
  };

  // Spectator Window öffnen
  const openSpectatorWindow = () => {
    const win = window.open('', 'FormenSpectator', 'width=1920,height=1080');
    setSpectatorWindow(win);
    updateSpectatorWindow(win);
  };

  // Spectator Window aktualisieren
  const updateSpectatorWindow = (win = spectatorWindow) => {
    if (!win || win.closed) return;

    const teilnehmer = currentTeilnehmer;
    const kategorie = selectedKategorie;

    win.document.body.innerHTML = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          background: linear-gradient(180deg, #0d0a07 0%, #1a1510 50%, #0d0a07 100%);
          color: #fff;
          font-family: 'Segoe UI', sans-serif;
          min-height: 100vh;
          overflow: hidden;
        }
        @keyframes floatUp {
          0% { transform: translateY(0) rotate(0deg); opacity: 0.08; }
          50% { opacity: 0.15; }
          100% { transform: translateY(-100vh) rotate(10deg); opacity: 0; }
        }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 30px rgba(212,175,55,0.5); } 50% { box-shadow: 0 0 60px rgba(212,175,55,0.8); } }
        .kanji {
          position: absolute;
          color: rgba(212,175,55,0.08);
          animation: floatUp 15s linear infinite;
          font-size: 8rem;
        }
        .corner { position: absolute; width: 60px; height: 60px; }
        .corner-h { position: absolute; height: 3px; background: linear-gradient(90deg, #d4af37, transparent); box-shadow: 0 0 10px rgba(212,175,55,0.5); }
        .corner-v { position: absolute; width: 3px; background: linear-gradient(180deg, #d4af37, transparent); box-shadow: 0 0 10px rgba(212,175,55,0.5); }
        .header {
          background: linear-gradient(180deg, #8B0000 0%, #5c0000 100%);
          padding: 1.5rem 3rem;
          border-bottom: 4px solid #d4af37;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .header-corners { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; pointer-events: none; }
        .header-corners::before, .header-corners::after { content: ''; position: absolute; width: 20px; height: 20px; border: 2px solid #d4af37; }
        .header-corners::before { top: 0; left: 0; border-right: none; border-bottom: none; }
        .header-corners::after { top: 0; right: 0; border-left: none; border-bottom: none; }
        .kategorie-badge {
          background: linear-gradient(135deg, rgba(212,175,55,0.2), rgba(92,64,51,0.3));
          border: 2px solid #d4af37;
          padding: 0.8rem 1.5rem;
          border-radius: 4px;
        }
        .kategorie-label { color: #888; font-size: 0.8rem; letter-spacing: 2px; }
        .kategorie-name { color: #d4af37; font-weight: 700; font-size: 1.3rem; }
        .title-center { text-align: center; }
        .title-sub { color: #d4af37; font-size: 0.9rem; letter-spacing: 4px; }
        .title-main { color: #fff; font-size: 2rem; font-weight: 700; letter-spacing: 3px; }
        .timer-box {
          background: rgba(0,0,0,0.4);
          border: 2px solid ${timerRunning ? '#4CAF50' : '#d4af37'};
          padding: 0.8rem 2rem;
          border-radius: 8px;
          text-align: center;
          animation: ${timerRunning ? 'pulse 1s infinite' : 'none'};
        }
        .timer-label { color: #888; font-size: 0.7rem; letter-spacing: 2px; }
        .timer-value { color: ${timerSeconds < 30 ? '#f44336' : '#d4af37'}; font-size: 2.5rem; font-weight: 700; font-family: monospace; }
        .main-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: calc(100vh - 150px);
          padding: 3rem;
          position: relative;
        }
        .athlete-card {
          background: linear-gradient(180deg, rgba(139,0,0,0.3) 0%, rgba(92,0,0,0.2) 100%);
          border: 3px solid #d4af37;
          border-radius: 16px;
          padding: 3rem 5rem;
          text-align: center;
          position: relative;
          animation: glow 3s ease-in-out infinite;
        }
        .athlete-number {
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #d4af37, #b8942d);
          color: #1a1510;
          padding: 0.5rem 2rem;
          border-radius: 30px;
          font-size: 1.5rem;
          font-weight: 900;
        }
        .athlete-name {
          font-size: 4rem;
          font-weight: 900;
          color: #fff;
          margin: 1rem 0;
          text-shadow: 0 0 30px rgba(255,255,255,0.3);
        }
        .athlete-details {
          display: flex;
          justify-content: center;
          gap: 3rem;
          color: #c4b89a;
          font-size: 1.3rem;
        }
        .status-text {
          margin-top: 2rem;
          font-size: 1.5rem;
          letter-spacing: 4px;
          color: ${performanceStarted ? '#4CAF50' : '#888'};
        }
        .no-athlete {
          text-align: center;
          color: #888;
        }
        .no-athlete-icon { font-size: 6rem; margin-bottom: 1rem; }
        .no-athlete-text { font-size: 2rem; color: #d4af37; }
      </style>

      <!-- Floating Kanji -->
      <div class="kanji" style="left: 5%; top: 100%; animation-delay: 0s;">形</div>
      <div class="kanji" style="left: 20%; top: 100%; animation-delay: 2s;">武</div>
      <div class="kanji" style="left: 35%; top: 100%; animation-delay: 4s;">道</div>
      <div class="kanji" style="left: 50%; top: 100%; animation-delay: 6s;">心</div>
      <div class="kanji" style="left: 65%; top: 100%; animation-delay: 8s;">技</div>
      <div class="kanji" style="left: 80%; top: 100%; animation-delay: 10s;">勝</div>

      <!-- Corners -->
      <div class="corner" style="top: 20px; left: 20px;">
        <div class="corner-h" style="top: 0; left: 0; width: 60px;"></div>
        <div class="corner-v" style="top: 0; left: 0; height: 60px;"></div>
      </div>
      <div class="corner" style="top: 20px; right: 20px;">
        <div class="corner-h" style="top: 0; right: 0; width: 60px; background: linear-gradient(270deg, #d4af37, transparent);"></div>
        <div class="corner-v" style="top: 0; right: 0; height: 60px;"></div>
      </div>
      <div class="corner" style="bottom: 20px; left: 20px;">
        <div class="corner-h" style="bottom: 0; left: 0; width: 60px;"></div>
        <div class="corner-v" style="bottom: 0; left: 0; height: 60px; background: linear-gradient(0deg, #d4af37, transparent);"></div>
      </div>
      <div class="corner" style="bottom: 20px; right: 20px;">
        <div class="corner-h" style="bottom: 0; right: 0; width: 60px; background: linear-gradient(270deg, #d4af37, transparent);"></div>
        <div class="corner-v" style="bottom: 0; right: 0; height: 60px; background: linear-gradient(0deg, #d4af37, transparent);"></div>
      </div>

      <!-- Header -->
      <div class="header" style="position: relative;">
        <div class="header-corners"></div>
        <div class="kategorie-badge">
          <div class="kategorie-label">部門 KATEGORIE</div>
          <div class="kategorie-name">${kategorie?.kategorie_name || 'Keine Kategorie'}</div>
        </div>
        <div class="title-center">
          <div class="title-sub">形 FORMEN</div>
          <div class="title-main">WERTUNG</div>
        </div>
        <div class="timer-box">
          <div class="timer-label">時間 ZEIT</div>
          <div class="timer-value">${formatTime(timerSeconds)}</div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content">
        ${teilnehmer ? `
          <div class="athlete-card">
            <div class="athlete-number">#${teilnehmer.startnummer}</div>
            <div class="athlete-name">${esc(teilnehmer.vorname)} ${esc(teilnehmer.nachname)}</div>
            <div class="athlete-details">
              <span>🏛️ ${esc(teilnehmer.verein_name)}</span>
              <span>🥋 ${esc(teilnehmer.gurtfarbe || 'Unbekannt')}</span>
            </div>
            <div class="status-text">
              ${performanceStarted ? (timerRunning ? '演武中 PERFORMING' : '⏸ PAUSIERT') : '準備完了 BEREIT'}
            </div>
          </div>
        ` : `
          <div class="no-athlete">
            <div class="no-athlete-icon">✅</div>
            <div class="no-athlete-text">ALLE BEWERTET</div>
          </div>
        `}
      </div>
    `;
  };

  // Show result on spectator
  const showResultOnSpectator = (punkte) => {
    if (!spectatorWindow || spectatorWindow.closed) return;

    const teilnehmerName = `${esc(currentTeilnehmer?.vorname)} ${esc(currentTeilnehmer?.nachname)}`;

    spectatorWindow.document.body.innerHTML = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          background: linear-gradient(180deg, #0d0a07 0%, #1a1510 50%, #0d0a07 100%);
          color: #fff;
          font-family: 'Segoe UI', sans-serif;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        @keyframes scaleIn { from { transform: scale(0); } to { transform: scale(1); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes glow { 0%, 100% { text-shadow: 0 0 30px rgba(212,175,55,0.5); } 50% { text-shadow: 0 0 60px rgba(212,175,55,1); } }
        .result-container { text-align: center; animation: fadeInUp 0.8s ease-out; }
        .result-label { color: #d4af37; font-size: 2rem; letter-spacing: 8px; margin-bottom: 1rem; }
        .result-name { font-size: 3rem; font-weight: 700; margin-bottom: 2rem; }
        .result-score {
          font-size: 12rem;
          font-weight: 900;
          color: #d4af37;
          animation: glow 2s ease-in-out infinite, scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .result-unit { font-size: 2rem; color: #888; margin-top: 1rem; letter-spacing: 4px; }
      </style>
      <div class="result-container">
        <div class="result-label">結果 ERGEBNIS</div>
        <div class="result-name">${teilnehmerName}</div>
        <div class="result-score">${punkte.toFixed(2)}</div>
        <div class="result-unit">PUNKTE</div>
      </div>
    `;

    // Nach 5 Sekunden zurück zur normalen Ansicht (nächster Kämpfer)
    setTimeout(() => {
      updateSpectatorWindow();
      // Nach weiteren 10 Sekunden (15 total) zur Rangliste wechseln
      setTimeout(() => showRankingOnSpectator(), 10000);
    }, 5000);
  };

  // Rangliste auf Spectator anzeigen
  const showRankingOnSpectator = () => {
    if (!spectatorWindow || spectatorWindow.closed) return;

    const completed = kategorieDetails?.teilnehmer
      ?.filter(tn => tn.status === 'completed')
      ?.sort((a, b) => b.gesamt_punkte - a.gesamt_punkte) || [];

    if (completed.length === 0) return;

    const rankingHTML = completed.slice(0, 10).map((tn, idx) => `
      <div class="rank-item ${idx < 3 ? 'top-' + (idx + 1) : ''}">
        <span class="rank-pos">${idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : (idx + 1) + '.'}</span>
        <span class="rank-name">${esc(tn.vorname)} ${esc(tn.nachname)}</span>
        <span class="rank-verein">${esc(tn.verein_name)}</span>
        <span class="rank-score">${parseFloat(tn.gesamt_punkte || 0).toFixed(2)}</span>
      </div>
    `).join('');

    spectatorWindow.document.body.innerHTML = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          background: linear-gradient(180deg, #0d0a07 0%, #1a1510 50%, #0d0a07 100%);
          color: #fff;
          font-family: 'Segoe UI', sans-serif;
          min-height: 100vh;
          padding: 2rem;
          overflow: hidden;
        }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
        .ranking-container { max-width: 1200px; margin: 0 auto; }
        .ranking-header {
          text-align: center;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: linear-gradient(180deg, #8B0000 0%, #5c0000 100%);
          border: 3px solid #d4af37;
          border-radius: 12px;
        }
        .ranking-title { color: #d4af37; font-size: 1.5rem; letter-spacing: 6px; }
        .ranking-subtitle { color: #fff; font-size: 2.5rem; font-weight: 700; margin-top: 0.5rem; }
        .kategorie-name { color: #c4b89a; font-size: 1.2rem; margin-top: 0.5rem; }
        .rank-list { display: flex; flex-direction: column; gap: 0.8rem; }
        .rank-item {
          display: grid;
          grid-template-columns: 80px 1fr 200px 120px;
          align-items: center;
          padding: 1rem 1.5rem;
          background: rgba(0,0,0,0.4);
          border: 2px solid #5c4033;
          border-radius: 8px;
          animation: slideIn 0.5s ease-out backwards;
        }
        .rank-item:nth-child(1) { animation-delay: 0.1s; }
        .rank-item:nth-child(2) { animation-delay: 0.2s; }
        .rank-item:nth-child(3) { animation-delay: 0.3s; }
        .rank-item:nth-child(4) { animation-delay: 0.4s; }
        .rank-item:nth-child(5) { animation-delay: 0.5s; }
        .top-1 { border-color: #FFD700; background: rgba(255,215,0,0.1); }
        .top-2 { border-color: #C0C0C0; background: rgba(192,192,192,0.1); }
        .top-3 { border-color: #CD7F32; background: rgba(205,127,50,0.1); }
        .rank-pos { font-size: 2rem; font-weight: 700; }
        .rank-name { font-size: 1.5rem; font-weight: 600; color: #fff; }
        .rank-verein { color: #888; font-size: 1rem; }
        .rank-score { font-size: 1.8rem; font-weight: 700; color: #d4af37; text-align: right; }
      </style>
      <div class="ranking-container">
        <div class="ranking-header">
          <div class="ranking-title">勝 AKTUELLE RANGLISTE</div>
          <div class="ranking-subtitle">WERTUNGSREIHENFOLGE</div>
          <div class="kategorie-name">${selectedKategorie?.kategorie_name || ''}</div>
        </div>
        <div class="rank-list">
          ${rankingHTML}
        </div>
      </div>
    `;

    // Nach 10 Sekunden zurück zum nächsten Kämpfer
    setTimeout(() => updateSpectatorWindow(), 10000);
  };

  // Winner-Screen auf Spectator anzeigen
  const showWinnerOnSpectator = () => {
    if (!spectatorWindow || spectatorWindow.closed) return;

    const completed = kategorieDetails?.teilnehmer
      ?.filter(tn => tn.status === 'completed')
      ?.sort((a, b) => b.gesamt_punkte - a.gesamt_punkte) || [];

    const gold = completed[0];
    const silver = completed[1];
    const bronze = completed[2];

    spectatorWindow.document.body.innerHTML = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          background: linear-gradient(180deg, #0d0a07 0%, #1a1510 50%, #0d0a07 100%);
          color: #fff;
          font-family: 'Segoe UI', sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 30px rgba(212,175,55,0.5); } 50% { box-shadow: 0 0 60px rgba(212,175,55,0.8); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(50px); } to { opacity: 1; transform: translateY(0); } }
        .winner-header {
          text-align: center;
          margin-bottom: 3rem;
          animation: fadeInUp 0.8s ease-out;
        }
        .winner-kanji { font-size: 4rem; color: #d4af37; margin-bottom: 0.5rem; }
        .winner-title { font-size: 3rem; font-weight: 700; letter-spacing: 8px; }
        .kategorie { color: #888; font-size: 1.5rem; margin-top: 0.5rem; }
        .podium { display: flex; align-items: flex-end; gap: 2rem; }
        .place {
          text-align: center;
          padding: 2rem;
          border-radius: 16px;
          animation: fadeInUp 0.8s ease-out backwards;
        }
        .place-1 {
          order: 2;
          background: linear-gradient(180deg, rgba(255,215,0,0.2), rgba(255,215,0,0.05));
          border: 3px solid #FFD700;
          animation-delay: 0.3s;
          animation: glow 3s ease-in-out infinite;
        }
        .place-2 {
          order: 1;
          background: linear-gradient(180deg, rgba(192,192,192,0.2), rgba(192,192,192,0.05));
          border: 3px solid #C0C0C0;
          animation-delay: 0.5s;
        }
        .place-3 {
          order: 3;
          background: linear-gradient(180deg, rgba(205,127,50,0.2), rgba(205,127,50,0.05));
          border: 3px solid #CD7F32;
          animation-delay: 0.7s;
        }
        .medal { font-size: 5rem; animation: float 2s ease-in-out infinite; }
        .place-1 .medal { animation-delay: 0s; }
        .place-2 .medal { animation-delay: 0.3s; }
        .place-3 .medal { animation-delay: 0.6s; }
        .place-name { font-size: 2rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        .place-verein { color: #888; font-size: 1rem; margin-bottom: 1rem; }
        .place-score { font-size: 2.5rem; font-weight: 900; color: #d4af37; }
        .place-label { color: #888; font-size: 0.9rem; letter-spacing: 2px; }
      </style>
      <div class="winner-header">
        <div class="winner-kanji">勝者</div>
        <div class="winner-title">SIEGEREHRUNG</div>
        <div class="kategorie">${selectedKategorie?.kategorie_name || ''}</div>
      </div>
      <div class="podium">
        ${silver ? `
          <div class="place place-2">
            <div class="medal">🥈</div>
            <div class="place-name">${esc(silver.vorname)} ${esc(silver.nachname)}</div>
            <div class="place-verein">${esc(silver.verein_name)}</div>
            <div class="place-score">${silver.gesamt_punkte?.toFixed(2)}</div>
            <div class="place-label">SILBER</div>
          </div>
        ` : ''}
        ${gold ? `
          <div class="place place-1">
            <div class="medal">🥇</div>
            <div class="place-name">${esc(gold.vorname)} ${esc(gold.nachname)}</div>
            <div class="place-verein">${esc(gold.verein_name)}</div>
            <div class="place-score">${gold.gesamt_punkte?.toFixed(2)}</div>
            <div class="place-label">GOLD</div>
          </div>
        ` : ''}
        ${bronze ? `
          <div class="place place-3">
            <div class="medal">🥉</div>
            <div class="place-name">${esc(bronze.vorname)} ${esc(bronze.nachname)}</div>
            <div class="place-verein">${esc(bronze.verein_name)}</div>
            <div class="place-score">${bronze.gesamt_punkte?.toFixed(2)}</div>
            <div class="place-label">BRONZE</div>
          </div>
        ` : ''}
      </div>
    `;
  };

  // Update spectator when timer changes
  useEffect(() => {
    if (spectatorWindow && !spectatorWindow.closed) {
      updateSpectatorWindow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerSeconds, timerRunning, performanceStarted, currentTeilnehmer]);

  const gesamtPunkte = wertungen.reduce((sum, w) => sum + (parseFloat(w.punkte) || 0), 0);

  // Nächster Wettkämpfer (noch nicht bewertet, nach Startnummer)
  const naechsterTeilnehmer = kategorieDetails?.teilnehmer
    ?.filter(tn => tn.wettkaempfer_id !== currentTeilnehmer?.wettkaempfer_id
                && tn.status !== 'completed'
                && tn.status !== 'disqualified')
    ?.sort((a, b) => (a.startnummer || 999) - (b.startnummer || 999))[0] || null;

  // Alle Wertungen ausgefüllt?
  const alleWertungenFilled = wertungen.length > 0
    && wertungen.every(w => w.punkte !== '' && !isNaN(parseFloat(w.punkte)));

  // Rangliste: bevorzuge liveRangliste (aus /rangliste-API, enthält auch Teilscores)
  // Fallback auf kategorieDetails falls noch kein Live-Load passiert ist
  const rangliste = liveRangliste.length > 0
    ? liveRangliste
    : (kategorieDetails?.teilnehmer
        ?.filter(tn => tn.status === 'completed')
        ?.sort((a, b) => b.gesamt_punkte - a.gesamt_punkte) || []);

  if (loading) {
    return (
      <div className="formen-loading-jp">
        <div className="loading-kanji">🥋</div>
        <p>{t('admin.formen_schreiber.loading')}</p>
      </div>
    );
  }

  return (
    <div className="formen-schreiber-jp">
      {/* Header - Japanese Style */}
      <div className="schreiber-header-jp">
        {/* Left: Back + Kategorie + Filter-Tabs + Kämpfer-Button */}
        <div className="header-left-jp">
          <button className="btn-back-jp" onClick={handleBack} title={t('admin.formen_schreiber.btn_back_title')}>
            {t('admin.formen_schreiber.btn_back')}
          </button>
          <div className="kategorie-select-jp">
            <span className="select-label">Kategorie</span>
            <select
              value={selectedKategorie?.kategorie_id || ''}
              onChange={(e) => handleKategorieSelect(parseInt(e.target.value))}
              size={1}
            >
              {(() => {
                const list = katFilter === 'alle' ? allKategorien
                  : katFilter === 'abgeschlossen' ? allKategorien.filter(k => k.status === 'completed')
                  : kategorien;
                return list.length === 0
                  ? <option value="">{t('admin.formen_schreiber.no_categories')}</option>
                  : list.map(kat => (
                      <option key={kat.kategorie_id} value={kat.kategorie_id}>
                        {kat.status === 'completed' ? '✅ ' : ''}{kat.kategorie_name} ({kat.abgeschlossen || 0}/{kat.teilnehmer_anzahl})
                      </option>
                    ));
              })()}
            </select>
          </div>

          {/* Kategorie-Filter-Tabs */}
          <div className="header-kat-filter-jp">
            {[['offen','Offen'],['abgeschlossen','Fertig'],['alle','Alle']].map(([val, label]) => (
              <button
                key={val}
                className={`header-filter-tab-jp ${katFilter === val ? 'active' : ''}`}
                onClick={() => setKatFilter(val)}
              >{label}</button>
            ))}
          </div>

          {/* Kämpfer hinzufügen */}
          {selectedKategorie && (
            <button className="btn-action-jp add" onClick={handleOpenAddFighter} title="Wettkämpfer zur Kategorie hinzufügen">
              + Kämpfer
            </button>
          )}
        </div>

        {/* Center: Timer (optional) */}
        <div className="header-timer-jp">
          {!timerEnabled ? (
            <span style={{fontSize:'0.8rem', color:'#aaa', fontStyle:'italic'}}>kein Timer</span>
          ) : (
            <>
              <span className={`timer-value-header ${timerStopped ? 'stopped' : timerSeconds < 30 && performanceStarted ? 'warning' : ''}`}>
                {formatTime(timerSeconds)}
              </span>
              <div className="timer-controls-header">
                {/* Start / Pause — nur wenn noch nicht gestoppt */}
                {!timerStopped && (
                  <button
                    className={`timer-btn-small ${performanceStarted && timerRunning ? 'pause' : 'start'}`}
                    onClick={handleTogglePerformance}
                    title={!performanceStarted ? 'Start' : timerRunning ? 'Pause' : 'Weiter'}
                  >
                    {!performanceStarted ? '▶' : (timerRunning ? '⏸' : '▶')}
                  </button>
                )}
                {/* Stop — nur wenn gestartet und noch nicht gestoppt */}
                {performanceStarted && !timerStopped && (
                  <button
                    className="timer-btn-small stop"
                    onClick={handleStopTimer}
                    title="Stop — Zeit einfrieren"
                  >
                    ⏹
                  </button>
                )}
                {/* Reset */}
                <button className="timer-btn-small reset" onClick={handleResetTimer} title="Zurücksetzen">
                  ↻
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: Actions */}
        <div className="header-actions-jp">
          <button className="btn-trophy-jp" onClick={() => setShowWinnerScreen(true)} title={t('admin.formen_schreiber.show_ceremony')}>
            🏆
          </button>
          <button className="btn-settings-jp" onClick={() => { setEditKampfrichter(selectedKategorie?.anzahl_kampfrichter || 3); setEditTimerDuration(timerDuration); setEditTimerEnabled(timerEnabled); setShowSettings(true); }} title={t('admin.formen_schreiber.settings')}>
            ⚙️
          </button>
          <button className="btn-spectator-jp" onClick={openSpectatorWindow} title={t('admin.formen_schreiber.open_second_screen')}>
            📺
          </button>
          <button className="btn-reset-jp" onClick={handleResetKategorie} title={t('admin.formen_schreiber.reset_category')}>
            🔄
          </button>
        </div>
      </div>

      {!selectedKategorie ? (
        <div className="no-kategorie-jp">
          <div className="empty-kanji">🥋</div>
          <h3>{t('admin.formen_schreiber.no_active_category')}</h3>
          {allKategorien.length === 0 && (
            <div className="auto-generate-section">
              <p className="auto-generate-hint">Noch keine Kategorien angelegt. Automatisch aus Anmeldungen generieren?</p>
              <button
                className="btn-auto-generate"
                onClick={handleAutoGenerate}
                disabled={autoGenerating}
              >
                {autoGenerating ? '⏳ Generiere...' : '⚡ Kategorien aus Anmeldungen generieren'}
              </button>
              {autoGenerateMsg && <p className="auto-generate-result">{autoGenerateMsg}</p>}
            </div>
          )}
        </div>
      ) : (
        <div className="schreiber-content-jp">
          {/* Left Panel - Wertung */}
          <div className="wertung-panel-jp">
            {/* Aktueller Athlet */}
            {currentTeilnehmer ? (
              <div className="athlete-card-jp">
                <div className="athlete-header-jp">
                  <span className="label-jp">Athlet</span>
                  <span className="startnummer-jp">#{currentTeilnehmer.startnummer}</span>
                </div>
                <div className="athlete-content-jp">
                  {/* Foto */}
                  <div className="athlete-photo-jp">
                    {currentTeilnehmer.foto_url ? (
                      <img src={currentTeilnehmer.foto_url} alt={currentTeilnehmer.vorname} />
                    ) : (
                      <div className="photo-placeholder-jp">
                        <span>🥋</span>
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="athlete-details-jp">
                    <div className="athlete-name-jp">
                      {currentTeilnehmer.vorname} {currentTeilnehmer.nachname}
                    </div>
                    <div className="athlete-info-jp">
                      <span>🏛️ {currentTeilnehmer.verein_name}</span>
                      <span>🥋 {currentTeilnehmer.gurtfarbe || '-'}</span>
                    </div>
                  </div>
                  {/* Gesamtpunkte */}
                  <div className="athlete-score-jp">
                    <span className="score-label-jp">Gesamt</span>
                    <span className="score-value-jp">{gesamtPunkte.toFixed(2)}</span>
                  </div>
                  {/* Disqualify Button */}
                  <button
                    className="btn-disqualify-jp"
                    onClick={() => { setDisqualifyTarget(currentTeilnehmer); setShowDisqualifyDialog(true); }}
                    title={t('admin.formen_schreiber.disqualify')}
                  >
                    🚫
                  </button>
                </div>
              </div>
            ) : (
              <div className="all-done-container-jp">
                <div className="all-done-icon">🏆</div>
                <div className="all-done-text">Alle bewertet!</div>
                <div className="all-done-buttons">
                  <button
                    className="show-winner-btn-jp"
                    onClick={() => setShowWinnerScreen(true)}
                  >
                    🎉 Siegerehrung anzeigen
                  </button>
                  <button
                    className="check-ties-btn-jp"
                    onClick={() => checkForMoreTies()}
                  >
                    ⚖️ Gleichstand prüfen
                  </button>
                </div>
                {rangliste.length > 0 && (
                  <div className="quick-results-jp">
                    <div className="quick-result gold">🥇 {rangliste[0]?.vorname} {rangliste[0]?.nachname} - {rangliste[0]?.gesamt_punkte?.toFixed(2)}</div>
                    {rangliste[1] && <div className="quick-result silver">🥈 {rangliste[1]?.vorname} {rangliste[1]?.nachname} - {rangliste[1]?.gesamt_punkte?.toFixed(2)}</div>}
                    {rangliste[2] && <div className="quick-result bronze">🥉 {rangliste[2]?.vorname} {rangliste[2]?.nachname} - {rangliste[2]?.gesamt_punkte?.toFixed(2)}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Wertungen */}
            {currentTeilnehmer && (
              <div className="wertung-grid-jp">
                <h3 className="section-title-jp">
                  Wertungen ({wertungen.length} Kampfrichter)
                </h3>

                <div className="kampfrichter-row-jp">
                  {wertungen.map((w, idx) => (
                    <div key={idx} className="kampfrichter-card-jp">
                      <div className="kr-header-jp">
                        <span className="kr-number-jp">KR {idx + 1}</span>
                      </div>
                      <input
                        type="number"
                        step="0.1"
                        value={w.punkte}
                        onChange={e => handlePunkteChange(idx, e.target.value)}
                        onBlur={e => {
                          if (e.target.value !== '' && !isNaN(parseFloat(e.target.value))) {
                            autoSaveWertung(idx + 1, e.target.value);
                            checkAndAutoComplete(wertungen);
                          }
                        }}
                        placeholder={`${selectedKategorie.punkte_min}-${selectedKategorie.punkte_max}`}
                        className="punkte-input-jp"
                      />
                      <div className="quick-btns-jp">
                        {(() => {
                          const pMin = parseFloat(selectedKategorie.punkte_min) || 6.0;
                          const pMax = parseFloat(selectedKategorie.punkte_max) || 10.0;
                          const isDan = pMin >= 8.0 && pMax >= 10.0;
                          const presets = isDan
                            ? [8.0, 8.5, 9.0, 9.5, 9.7, 10.0]
                            : (() => {
                                const arr = [];
                                for (let v = pMin; v <= pMax + 0.001; v = Math.round((v + 0.5) * 10) / 10) arr.push(Math.round(v * 10) / 10);
                                return arr;
                              })();
                          return presets.map(score => (
                            <button
                              key={score}
                              className={`quick-btn-jp ${parseFloat(w.punkte) === score ? 'active' : ''}`}
                              onClick={() => handleQuickScore(idx, score)}
                            >
                              {score.toFixed(1)}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ── Bottom: Speichern / Nächster Wettkämpfer ─────────────────── */}
            {currentTeilnehmer && (
              <div style={{marginTop:'1.5rem', borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:'1.25rem', display:'flex', flexDirection:'column', gap:'1rem'}}>

                {/* Nächster Wettkämpfer Vorschau */}
                {naechsterTeilnehmer && (
                  <div style={{display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderRadius:8, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.10)'}}>
                    <span style={{color:'rgba(255,255,255,0.45)', fontSize:'12px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', flexShrink:0}}>Nächster</span>
                    <span style={{color:'#fff', fontWeight:700, fontSize:'14px'}}>
                      #{naechsterTeilnehmer.startnummer} {naechsterTeilnehmer.vorname} {naechsterTeilnehmer.nachname}
                    </span>
                    {naechsterTeilnehmer.gurtfarbe && (
                      <span style={{fontSize:'12px', color:'rgba(255,255,255,0.5)', background:'rgba(255,255,255,0.08)', padding:'2px 8px', borderRadius:4}}>{naechsterTeilnehmer.gurtfarbe}</span>
                    )}
                  </div>
                )}
                {!naechsterTeilnehmer && (
                  <div style={{padding:'10px 16px', borderRadius:8, background:'rgba(39,174,96,0.10)', border:'1px solid rgba(39,174,96,0.25)', color:'#5dca7e', fontSize:'13px', fontWeight:600}}>
                    Letzter Wettkämpfer dieser Kategorie
                  </div>
                )}

                {/* Button-Reihe */}
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>

                  {/* Speichern — wenn alle Noten vergeben aber noch nicht gespeichert */}
                  {alleWertungenFilled && !wertungGespeichert && (
                    <button
                      onClick={() => checkAndAutoComplete(wertungen)}
                      style={{flex:'1 1 200px', padding:'12px 20px', borderRadius:8, border:'none', background:'#1a6b4a', color:'#fff', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}
                    >
                      💾 Wertung speichern
                    </button>
                  )}

                  {/* Wertung gespeichert Feedback */}
                  {wertungGespeichert && (
                    <div style={{flex:'1 1 200px', padding:'12px 20px', borderRadius:8, background:'rgba(39,174,96,0.15)', border:'1px solid rgba(39,174,96,0.35)', color:'#5dca7e', fontWeight:700, fontSize:'14px', display:'flex', alignItems:'center', gap:'8px'}}>
                      ✅ Wertung gespeichert ({gesamtPunkte.toFixed(2)} Pkt.)
                    </div>
                  )}

                  {/* Nächster Wettkämpfer — nach dem Speichern */}
                  {wertungGespeichert && naechsterTeilnehmer && (
                    <button
                      onClick={handleNaechsterTeilnehmer}
                      style={{flex:'1 1 200px', padding:'12px 20px', borderRadius:8, border:'none', background:'#8B0000', color:'#fff', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}
                    >
                      ▶ Nächster Wettkämpfer
                    </button>
                  )}

                  {/* Kategorie fertig */}
                  {wertungGespeichert && !naechsterTeilnehmer && (
                    <button
                      onClick={() => setShowWinnerScreen(true)}
                      style={{flex:'1 1 200px', padding:'12px 20px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#d4af37,#b8942d)', color:'#1a1510', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}
                    >
                      🏆 Siegerehrung anzeigen
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Lists */}
          <div className="sidebar-jp">
            {/* Startreihenfolge */}
            <div className="list-card-jp">
              <div className="list-header-jp">
                <h3 className="list-title-jp">Startreihenfolge</h3>
                <button
                  className="btn-action-jp"
                  onClick={handleShuffle}
                  disabled={kategorieGestartet}
                  title={kategorieGestartet ? t('admin.formen_schreiber.shuffle_not_possible') : t('admin.formen_schreiber.shuffle_order')}
                >🔀 Mischen</button>
              </div>

              {/* Filter-Tabs */}
              <div className="filter-tabs-jp">
                {[['alle','Alle'],['offen','Offen'],['abgeschlossen','Fertig']].map(([val, label]) => (
                  <button
                    key={val}
                    className={`filter-tab-jp ${filterStatus === val ? 'active' : ''}`}
                    onClick={() => setFilterStatus(val)}
                  >{label}</button>
                ))}
              </div>

              <div className="list-scroll-jp">
                {(kategorieDetails?.teilnehmer || [])
                  .slice()
                  .sort((a, b) => (a.startnummer || 999) - (b.startnummer || 999))
                  .filter(tn => {
                    if (filterStatus === 'offen') return tn.status !== 'completed' && tn.status !== 'disqualified';
                    if (filterStatus === 'abgeschlossen') return tn.status === 'completed' || tn.status === 'disqualified';
                    return true;
                  })
                  .map((tn) => (
                    <div
                      key={tn.wettkaempfer_id}
                      className={`list-item-jp ${tn.status} ${currentTeilnehmer?.wettkaempfer_id === tn.wettkaempfer_id ? 'active' : ''}`}
                      onClick={() => selectTeilnehmer(tn)}
                      title={tn.status === 'completed' ? t('admin.formen_schreiber.click_to_edit') : ''}
                    >
                      <span className="item-nummer-jp">{tn.startnummer}</span>
                      <span className="item-name-jp">{tn.vorname} {tn.nachname}</span>
                      <span className="item-status-jp">
                        {tn.status === 'completed' ? `✅ ${parseFloat(tn.endwertung || tn.gesamt_punkte || 0).toFixed(2)}` : tn.status === 'disqualified' ? '🚫' : '⏳'}
                      </span>
                      {tn.status === 'completed' && (
                        <button className="item-undo-btn-jp" onClick={(e) => resetTeilnehmerWertung(tn, e)} title={t('admin.formen_schreiber.reset_score')}>↩</button>
                      )}
                      <button className="item-undo-btn-jp" onClick={(e) => handleRemoveTeilnehmer(tn, e)} title="Aus Kategorie entfernen" style={{color:'#e55'}}>✕</button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Rangliste */}
            <div className="list-card-jp rangliste">
              <h3 className="list-title-jp">Rangliste</h3>
              {rangliste.length === 0 ? (
                <div className="no-results-jp">Noch keine Wertungen</div>
              ) : (
                <div className="list-scroll-jp">
                  {rangliste.map((tn, idx) => {
                    // Prüfen ob Gleichstand mit vorherigem oder nächstem (noch nicht aufgelöst)
                    const prevPunkte = idx > 0 ? Math.round((rangliste[idx-1]?.gesamt_punkte || 0) * 100) / 100 : null;
                    const currentPunkte = Math.round((tn.gesamt_punkte || 0) * 100) / 100;
                    const nextPunkte = idx < rangliste.length - 1 ? Math.round((rangliste[idx+1]?.gesamt_punkte || 0) * 100) / 100 : null;
                    const hasTie = (prevPunkte === currentPunkte || nextPunkte === currentPunkte) && isNotResolved(tn.tie_resolved);

                    // Prüfen ob Tie-Breaker aufgelöst wurde
                    const tieResolved = tn.tie_resolved && (tn.tie_resolved === true || tn.tie_resolved === 1 || tn.tie_resolved === '1');
                    const tieMethod = tn.tie_method;

                    // Gewinner oder Verlierer bei aufgelöstem Gleichstand?
                    // Gewinner: hat gleiche Punkte wie nächster aber bessere Platzierung
                    // Verlierer: hat gleiche Punkte wie vorheriger aber schlechtere Platzierung
                    let tieWinner = false;
                    let tieLoser = false;
                    if (tieResolved && tieMethod) {
                      if (nextPunkte === currentPunkte) {
                        tieWinner = true; // Bessere Platzierung trotz gleicher Punkte = Gewinner
                      }
                      if (prevPunkte === currentPunkte) {
                        tieLoser = true; // Schlechtere Platzierung trotz gleicher Punkte = Verlierer
                      }
                    }

                    // Symbol für Tie-Breaker Methode
                    const getTieSymbol = () => {
                      if (tieMethod === 'handwertung') {
                        return tieWinner ? '🖐️↑' : '🖐️↓';
                      } else if (tieMethod === 'extrapunkt') {
                        return tieWinner ? '➕↑' : '➕↓';
                      }
                      return null;
                    };

                    const tieSymbol = getTieSymbol();
                    const tieTitle = tieResolved
                      ? `${tieMethod === 'handwertung' ? t('admin.formen_schreiber.tie_hand') : t('admin.formen_schreiber.tie_extra')}: ${tieWinner ? t('admin.formen_schreiber.tie_won') : t('admin.formen_schreiber.tie_lost')}`
                      : hasTie ? 'Punktgleichheit!' : '';

                    return (
                      <div
                        key={tn.wettkaempfer_id}
                        className={`rang-item-jp platz-${idx + 1} ${hasTie ? 'has-tie' : ''} ${tieWinner ? 'tie-winner' : ''} ${tieLoser ? 'tie-loser' : ''}`}
                        title={tieTitle}
                      >
                        <span className="rang-platz-jp">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                        </span>
                        <span className="rang-name-jp">
                          {hasTie && <span className="tie-indicator">⚖️</span>}
                          {tieSymbol && <span className={`tie-result ${tieWinner ? 'winner' : 'loser'}`}>{tieSymbol}</span>}
                          {tn.vorname} {tn.nachname}
                        </span>
                        <span className="rang-punkte-jp">{parseFloat(tn.gesamt_punkte || 0).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="settings-modal-overlay-jp" onClick={() => setShowSettings(false)}>
          <div className="settings-modal-jp" onClick={e => e.stopPropagation()}>
            <div className="settings-header-jp">
              <span>Einstellungen</span>
              <button className="close-btn-jp" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            <div className="settings-content-jp">
              <div className="setting-row-jp">
                <label>Kampfrichter Anzahl:</label>
                <div className="kampfrichter-selector-jp">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      className={`kr-select-btn-jp ${editKampfrichter === n ? 'active' : ''}`}
                      onClick={() => setEditKampfrichter(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="setting-info-jp">
                Aktuell: {selectedKategorie?.anzahl_kampfrichter || 3} Kampfrichter
              </div>

              {/* Timer aktivieren/deaktivieren */}
              <div className="setting-row-jp" style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <label style={{ marginBottom: 0 }}>Maximalzeit (Timer):</label>
                <button
                  onClick={() => setEditTimerEnabled(v => !v)}
                  style={{
                    padding: '4px 14px', borderRadius: 6, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: editTimerEnabled ? 'rgba(76,175,80,0.15)' : 'rgba(0,0,0,0.07)',
                    color: editTimerEnabled ? '#2e7d32' : '#888',
                    outline: editTimerEnabled ? '1px solid rgba(76,175,80,0.4)' : '1px solid rgba(0,0,0,0.1)',
                  }}
                >
                  {editTimerEnabled ? '✓ Aktiv' : '✗ Deaktiviert'}
                </button>
                <span style={{ fontSize: '0.78rem', color: '#999' }}>
                  {editTimerEnabled ? 'Ablauf-Zeit wird angezeigt' : 'Kein Timer für diese Disziplin'}
                </span>
              </div>

              {/* Timer-Dauer (nur wenn aktiv) */}
              {editTimerEnabled && (
                <div className="setting-row-jp" style={{ marginTop: '1rem' }}>
                  <label>Maximale Zeit:</label>
                  <div className="kampfrichter-selector-jp">
                    {[60, 120, 180, 240, 300].map(sec => (
                      <button
                        key={sec}
                        className={`kr-select-btn-jp ${editTimerDuration === sec ? 'active' : ''}`}
                        onClick={() => setEditTimerDuration(sec)}
                      >
                        {sec / 60}m
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.82rem', color: '#888', minWidth: 90 }}>Freie Eingabe:</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      placeholder="Minuten"
                      value={editTimerDuration % 60 === 0 && editTimerDuration > 0 ? editTimerDuration / 60 : ''}
                      onChange={e => {
                        const mins = parseInt(e.target.value, 10);
                        if (!isNaN(mins) && mins >= 1 && mins <= 60) setEditTimerDuration(mins * 60);
                      }}
                      style={{ width: 80, padding: '0.3rem 0.5rem', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5, fontSize: '0.88rem', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '0.82rem', color: '#888' }}>Minuten</span>
                    <span style={{ fontSize: '0.82rem', color: '#cd3131', marginLeft: 4 }}>
                      → {Math.floor(editTimerDuration / 60)}:{String(editTimerDuration % 60).padStart(2, '0')} min
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="settings-footer-jp">
              <button className="cancel-btn-jp" onClick={() => setShowSettings(false)}>Abbrechen</button>
              <button className="save-settings-btn-jp" onClick={() => {
                handleUpdateKampfrichter(editKampfrichter);
                setTimerEnabled(editTimerEnabled);
                if (editTimerEnabled) {
                  setTimerDuration(editTimerDuration);
                  setTimerSeconds(editTimerDuration);
                  setTimerStopped(false);
                  setTimerRunning(false);
                  setPerformanceStarted(false);
                }
                setShowSettings(false);
              }}>
                保存 Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review-Phase Screen */}
      {showReviewScreen && (() => {
        const hasChanges = reviewHasChanges();
        return (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:2100,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
            <div style={{background:'#0d1117',borderRadius:16,width:'100%',maxWidth:680,maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 80px rgba(0,0,0,0.7)',fontFamily:'Inter,sans-serif',border:'1px solid rgba(212,175,55,0.2)'}}>

              {/* Header */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'1.1rem 1.5rem',background:'linear-gradient(135deg,rgba(212,175,55,0.12),rgba(212,175,55,0.06))',borderBottom:'1px solid rgba(212,175,55,0.2)',borderRadius:'16px 16px 0 0',flexShrink:0}}>
                <div>
                  <div style={{fontSize:'1.05rem',fontWeight:700,color:'#d4af37',letterSpacing:'0.04em'}}>📋 Rangliste überprüfen</div>
                  <div style={{fontSize:'0.78rem',color:'rgba(255,255,255,0.45)',marginTop:2}}>{selectedKategorie?.kategorie_name} · Alle Wettkämpfer bewertet</div>
                </div>
                <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.35)',textAlign:'right',maxWidth:200,lineHeight:1.4}}>
                  Reihenfolge prüfen und ggf. korrigieren.<br/>Dann Rangliste bestätigen.
                </div>
              </div>

              {/* Rangliste mit Auf/Ab-Buttons */}
              <div style={{overflowY:'auto',flex:1,padding:'1rem 1.5rem'}}>
                {reviewRangliste.map((tn, idx) => {
                  const origEntry = reviewOriginal.find(o => o.wettkaempfer_id === tn.wettkaempfer_id);
                  const changed = origEntry && origEntry.platzierung !== tn.platzierung;
                  const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
                  return (
                    <div key={tn.wettkaempfer_id} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.65rem 0.85rem',borderRadius:10,marginBottom:6,background:changed?'rgba(212,175,55,0.10)':'rgba(255,255,255,0.04)',border:`1px solid ${changed?'rgba(212,175,55,0.35)':'rgba(255,255,255,0.07)'}`,transition:'all 0.15s'}}>
                      {/* Rank */}
                      <div style={{minWidth:34,textAlign:'center',fontSize:'1.1rem'}}>
                        {medal || <span style={{color:'rgba(255,255,255,0.4)',fontSize:'0.9rem',fontWeight:600}}>{idx+1}.</span>}
                      </div>
                      {/* Name + Verein */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,color:changed?'#d4af37':'#fff',fontSize:'0.92rem',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {tn.vorname} {tn.nachname}
                          {changed && <span style={{marginLeft:8,fontSize:'0.7rem',color:'#d4af37',background:'rgba(212,175,55,0.15)',padding:'1px 6px',borderRadius:4}}>geändert</span>}
                        </div>
                        <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.35)',marginTop:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{tn.verein_name || '—'}</div>
                      </div>
                      {/* Punkte */}
                      <div style={{minWidth:52,textAlign:'right',fontWeight:700,color:'#5dca7e',fontSize:'0.9rem'}}>
                        {(parseFloat(tn.endwertung)||0).toFixed(2)}
                      </div>
                      {/* Buttons */}
                      <div style={{display:'flex',flexDirection:'column',gap:2}}>
                        <button onClick={() => handleReviewMove(idx, -1)} disabled={idx === 0}
                          style={{padding:'2px 7px',fontSize:'0.75rem',borderRadius:4,border:'1px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.06)',color:idx===0?'rgba(255,255,255,0.2)':'#fff',cursor:idx===0?'not-allowed':'pointer',lineHeight:1.4}}>▲</button>
                        <button onClick={() => handleReviewMove(idx, 1)} disabled={idx === reviewRangliste.length - 1}
                          style={{padding:'2px 7px',fontSize:'0.75rem',borderRadius:4,border:'1px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.06)',color:idx===reviewRangliste.length-1?'rgba(255,255,255,0.2)':'#fff',cursor:idx===reviewRangliste.length-1?'not-allowed':'pointer',lineHeight:1.4}}>▼</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Begründung (nur wenn geändert) */}
              {hasChanges && (
                <div style={{padding:'0 1.5rem 0.75rem',flexShrink:0}}>
                  <div style={{fontSize:'0.78rem',color:'#d4af37',fontWeight:600,marginBottom:4}}>
                    ✏️ Begründung für Rang-Korrektur <span style={{color:'#e55'}}>*</span>
                  </div>
                  <textarea
                    value={reviewBegruendung}
                    onChange={e => setReviewBegruendung(e.target.value)}
                    placeholder="z.B. Technischer Fehler wurde in Gesamtbetrachtung zu niedrig bewertet…"
                    rows={2}
                    style={{width:'100%',boxSizing:'border-box',background:'rgba(255,255,255,0.06)',border:`1px solid ${reviewBegruendung.trim().length>=5?'rgba(93,202,126,0.4)':'rgba(229,85,85,0.4)'}`,borderRadius:8,color:'#fff',padding:'0.6rem 0.8rem',fontSize:'0.83rem',fontFamily:'inherit',resize:'vertical',outline:'none'}}
                  />
                  <div style={{fontSize:'0.7rem',color:'rgba(255,255,255,0.3)',marginTop:3}}>
                    Diese Begründung wird protokolliert (SA-Lizenz-Dokumentation).
                  </div>
                </div>
              )}

              {/* Footer Buttons */}
              <div style={{display:'flex',gap:'0.75rem',padding:'0.85rem 1.5rem 1.25rem',borderTop:'1px solid rgba(255,255,255,0.07)',flexShrink:0}}>
                <button
                  onClick={() => setShowReviewScreen(false)}
                  style={{flex:'0 0 auto',padding:'0.65rem 1.2rem',borderRadius:8,border:'1px solid rgba(255,255,255,0.15)',background:'transparent',color:'rgba(255,255,255,0.55)',fontSize:'0.85rem',cursor:'pointer',fontFamily:'inherit'}}>
                  ← Zurück
                </button>
                <button
                  onClick={handleFinalize}
                  disabled={reviewSaving || (hasChanges && reviewBegruendung.trim().length < 5)}
                  style={{flex:1,padding:'0.65rem 1.2rem',borderRadius:8,border:'none',background: (reviewSaving||(hasChanges&&reviewBegruendung.trim().length<5)) ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#d4af37,#b8942d)',color: (reviewSaving||(hasChanges&&reviewBegruendung.trim().length<5)) ? 'rgba(255,255,255,0.3)' : '#1a1510',fontWeight:700,fontSize:'0.9rem',cursor:(reviewSaving||(hasChanges&&reviewBegruendung.trim().length<5))?'not-allowed':'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                  {reviewSaving ? '⏳ Wird gespeichert…' : hasChanges ? '✓ Korrigierte Rangliste bestätigen' : '✓ Rangliste bestätigen'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Siegerehrung Screen */}
      {showWinnerScreen && (() => {
        const MEDAL = {
          1: { emoji: '🥇', label: 'Gold',   bg: 'linear-gradient(135deg,#fbbf24,#d97706)', shadow: '0 6px 20px rgba(217,119,6,0.4)' },
          2: { emoji: '🥈', label: 'Silber', bg: 'linear-gradient(135deg,#e5e7eb,#9ca3af)', shadow: '0 6px 20px rgba(156,163,175,0.4)' },
          3: { emoji: '🥉', label: 'Bronze', bg: 'linear-gradient(135deg,#d97706,#92400e)', shadow: '0 6px 20px rgba(180,83,9,0.4)' },
        };
        const podiumHeights = { 1: 165, 2: 130, 3: 110 };
        // Podium: Platz 2 links, 1 Mitte, 3 rechts
        const top3 = [
          rangliste.find(r => (r.platzierung || rangliste.indexOf(r) + 1) === 2),
          rangliste.find(r => (r.platzierung || rangliste.indexOf(r) + 1) === 1),
          rangliste.find(r => (r.platzierung || rangliste.indexOf(r) + 1) === 3),
        ].filter(Boolean);
        const rest = rangliste.filter(r => (r.platzierung || rangliste.indexOf(r) + 1) > 3);
        return (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}
               onClick={() => setShowWinnerScreen(false)}>
            <div style={{background:'#0a0a14',borderRadius:16,width:'100%',maxWidth:700,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 80px rgba(0,0,0,0.6)',fontFamily:'Inter,sans-serif'}}
                 onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'1.25rem 1.5rem',background:'rgba(255,255,255,0.04)',borderBottom:'1px solid rgba(255,255,255,0.07)',borderRadius:'16px 16px 0 0'}}>
                <div>
                  <div style={{fontSize:'1.1rem',fontWeight:700,color:'#fff',letterSpacing:'0.05em'}}>🏆 Siegerehrung</div>
                  <div style={{fontSize:'0.8rem',color:'rgba(255,255,255,0.45)',marginTop:2}}>{selectedKategorie?.kategorie_name}</div>
                </div>
                <button onClick={() => setShowWinnerScreen(false)}
                  style={{background:'transparent',border:'1px solid rgba(255,255,255,0.15)',borderRadius:8,color:'rgba(255,255,255,0.6)',padding:'0.4rem 0.85rem',fontSize:'0.82rem',cursor:'pointer',fontFamily:'inherit'}}>
                  ✕ Schließen
                </button>
              </div>

              {/* Podium */}
              {top3.length > 0 && (
                <div style={{display:'flex',alignItems:'flex-end',justifyContent:'center',gap:'1.5rem',padding:'2rem 1.5rem 0'}}>
                  {top3.map(tn => {
                    const rank = tn.platzierung || (rangliste.indexOf(tn) + 1);
                    const m = MEDAL[rank];
                    const h = podiumHeights[rank] || 100;
                    return (
                      <div key={tn.wettkaempfer_id} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'0.5rem'}}>
                        <div style={{fontSize:'2.2rem'}}>{m?.emoji || rank}</div>
                        <div style={{textAlign:'center',maxWidth:140}}>
                          <div style={{fontSize:'0.9rem',fontWeight:700,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{tn.vorname} {tn.nachname}</div>
                          <div style={{fontSize:'0.72rem',color:'rgba(255,255,255,0.4)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{tn.verein_name || '-'}</div>
                          <div style={{fontSize:'0.78rem',fontWeight:700,color:'#f59e0b',marginTop:2}}>{tn.gesamt_punkte?.toFixed(2)} Pkt</div>
                        </div>
                        <div style={{width:120,height:h,background:m?.bg || 'rgba(255,255,255,0.1)',borderRadius:'8px 8px 0 0',boxShadow:m?.shadow,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:'0.6rem'}}>
                          <span style={{fontSize:'1.4rem',fontWeight:900,color:'rgba(255,255,255,0.9)'}}>{rank}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Full Rankings */}
              <div style={{padding:'1.5rem'}}>
                {rangliste.length === 0 && (
                  <div style={{textAlign:'center',color:'rgba(255,255,255,0.4)',padding:'2rem'}}>Noch keine Wertungen</div>
                )}
                <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                  {rangliste.map((tn, idx) => {
                    const rank = tn.platzierung || idx + 1;
                    const m = MEDAL[rank];
                    return (
                      <div key={tn.wettkaempfer_id} style={{display:'flex',alignItems:'center',gap:'1rem',padding:'0.75rem 1rem',background:'rgba(255,255,255,0.04)',borderRadius:10,border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div style={{width:36,height:36,borderRadius:'50%',background:m?.bg||'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',flexShrink:0}}>
                          {m?.emoji || rank}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'0.9rem',fontWeight:600,color:'#fff'}}>{tn.vorname} {tn.nachname}</div>
                          <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)'}}>{tn.verein_name || '-'}</div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <div style={{fontSize:'0.78rem',fontWeight:700,color:m ? ['#f59e0b','#9ca3af','#b45309'][rank-1] : 'rgba(255,255,255,0.35)'}}>{m?.label || (rank + '. Platz')}</div>
                          <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)'}}>{tn.gesamt_punkte?.toFixed(2)} Pkt</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div style={{padding:'1rem 1.5rem',borderTop:'1px solid rgba(255,255,255,0.07)',textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'0.78rem'}}>
                Klicken außerhalb oder ✕ zum Schließen
              </div>
            </div>
          </div>
        );
      })()}

      {/* Disqualifikation Dialog */}
      {showDisqualifyDialog && disqualifyTarget && (
        <div className="modal-overlay-jp" onClick={() => setShowDisqualifyDialog(false)}>
          <div className="modal-content-jp disqualify-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header-jp disqualify">
              <span className="modal-icon">🚫</span>
              <span>DISQUALIFIKATION</span>
            </div>
            <div className="modal-body-jp">
              <div className="disqualify-target">
                <span className="target-label">Athlet:</span>
                <span className="target-name">{disqualifyTarget.vorname} {disqualifyTarget.nachname}</span>
              </div>
              <div className="disqualify-reason-input">
                <label>{t('admin.formen_schreiber.reason_label')}</label>
                <select
                  value={disqualifyReason}
                  onChange={e => setDisqualifyReason(e.target.value)}
                  className="reason-select-jp"
                >
                  <option value="">-- Grund wählen --</option>
                  <option value="Regelverstoß">{t('admin.formen_schreiber.reason_violation')}</option>
                  <option value="Unsportliches Verhalten">{t('admin.formen_schreiber.reason_unsporting')}</option>
                  <option value="Nicht angetreten">{t('admin.formen_schreiber.reason_no_show')}</option>
                  <option value="Verletzung">{t('admin.formen_schreiber.reason_injury')}</option>
                  <option value="Technischer Ausschluss">{t('admin.formen_schreiber.reason_technical')}</option>
                  <option value="Sonstiges">{t('admin.formen_schreiber.reason_other')}</option>
                </select>
                {disqualifyReason === 'Sonstiges' && (
                  <input
                    type="text"
                    placeholder="Grund eingeben..."
                    className="reason-input-jp"
                    onChange={e => setDisqualifyReason(e.target.value)}
                  />
                )}
              </div>
            </div>
            <div className="modal-footer-jp">
              <button className="cancel-btn-jp" onClick={() => { setShowDisqualifyDialog(false); setDisqualifyReason(''); }}>
                Abbrechen
              </button>
              <button className="confirm-disqualify-btn-jp" onClick={handleDisqualify} disabled={!disqualifyReason}>
                🚫 Disqualifizieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wettkämpfer hinzufügen Modal */}
      {showAddFighter && (() => {
        const existingIds = new Set(kategorieDetails?.teilnehmer?.map(t => t.wettkaempfer_id) || []);
        const q = addFighterSearch.toLowerCase();
        const filteredFighters = addFighterAll.filter(wk => {
          if (existingIds.has(wk.wettkaempfer_id)) return false;
          if (!q) return true;
          return `${wk.vorname} ${wk.nachname}`.toLowerCase().includes(q)
            || (wk.verein_name || '').toLowerCase().includes(q);
        });
        return (
          <div className="settings-modal-overlay-jp" onClick={() => setShowAddFighter(false)}>
            <div
              className="settings-modal-jp"
              onClick={e => e.stopPropagation()}
              style={{maxWidth: 460, maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)'}}
            >
              <div className="settings-header-jp" style={{background: '#161b22', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.08)'}}>
                <span>Wettkämpfer hinzufügen</span>
                <button className="close-btn-jp" onClick={() => setShowAddFighter(false)} style={{color: 'rgba(255,255,255,0.6)', background: 'transparent', border: 'none', fontSize: '1rem', cursor: 'pointer'}}>✕</button>
              </div>

              {/* Suchfeld */}
              <div style={{padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)'}}>
                <input
                  type="text"
                  placeholder="Name oder Verein suchen…"
                  value={addFighterSearch}
                  onChange={e => setAddFighterSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem',
                    borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: '0.9rem',
                    outline: 'none', fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Liste */}
              <div style={{flex: 1, overflowY: 'auto', padding: '0.5rem 1rem'}}>
                {addFighterLoading ? (
                  <div style={{textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.4)'}}>Lade…</div>
                ) : filteredFighters.length === 0 ? (
                  <div style={{textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem'}}>
                    {addFighterSearch ? 'Keine Treffer' : 'Alle verfügbaren Kämpfer sind bereits in dieser Kategorie'}
                  </div>
                ) : filteredFighters.map(wk => {
                  const isSelected = addFighterSelected.includes(wk.wettkaempfer_id);
                  return (
                    <div
                      key={wk.wettkaempfer_id}
                      onClick={() => setAddFighterSelected(prev =>
                        isSelected ? prev.filter(id => id !== wk.wettkaempfer_id) : [...prev, wk.wettkaempfer_id]
                      )}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        padding: '0.5rem 0.6rem', borderRadius: 7, marginBottom: 4,
                        cursor: 'pointer',
                        background: isSelected ? 'rgba(212,175,55,0.13)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isSelected ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.07)'}`,
                        transition: 'all 0.1s'
                      }}
                    >
                      <div style={{
                        width: 17, height: 17, borderRadius: 4, flexShrink: 0,
                        background: isSelected ? '#d4af37' : 'transparent',
                        border: `2px solid ${isSelected ? '#d4af37' : 'rgba(255,255,255,0.28)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#1a1510', fontSize: '0.65rem', fontWeight: 900
                      }}>
                        {isSelected ? '✓' : ''}
                      </div>
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontWeight: 600, color: isSelected ? '#d4af37' : '#fff', fontSize: '0.87rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                          {wk.vorname} {wk.nachname}
                        </div>
                        <div style={{fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                          {wk.verein_name || '—'}{wk.gurtfarbe ? ` · ${wk.gurtfarbe}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="settings-footer-jp" style={{borderTop: '1px solid rgba(255,255,255,0.08)'}}>
                <button className="cancel-btn-jp" onClick={() => setShowAddFighter(false)} style={{color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)'}}>Abbrechen</button>
                <button
                  className="save-settings-btn-jp"
                  onClick={handleAddFighters}
                  disabled={addFighterSelected.length === 0 || addFighterSaving}
                  style={{
                    background: 'linear-gradient(135deg,#d4af37,#b8942d)',
                    color: '#1a1510',
                    ...(addFighterSelected.length === 0 ? {opacity: 0.35, cursor: 'not-allowed'} : {})
                  }}
                >
                  {addFighterSaving ? '⏳ Speichere…' : `+ ${addFighterSelected.length || ''} Hinzufügen`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tie-Breaker Dialog (Punktgleichheit) */}
      {showTieBreakerDialog && tieBreakerData && (
        <div className="modal-overlay-jp">
          <div className="modal-content-jp tiebreaker-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header-jp tiebreaker">
              <span className="modal-icon">⚖️</span>
              <span>PUNKTGLEICHHEIT</span>
            </div>
            <div className="modal-body-jp">
              <div className="tiebreaker-info">
                <p>Für Platz <strong>{tieBreakerData.platzierung}</strong> haben folgende Athleten die gleiche Punktzahl:</p>
                <p className="tied-score">{tieBreakerData.punkte?.toFixed(2)} Punkte</p>
              </div>
              <div className="tiebreaker-participants">
                {tieBreakerData.teilnehmer.map(tn => (
                  <div key={tn.wettkaempfer_id} className="tied-participant">
                    <span className="tied-name">{tn.vorname} {tn.nachname}</span>
                    <span className="tied-verein">{tn.verein_name}</span>
                  </div>
                ))}
              </div>
              <div className="tiebreaker-question">
                <p>Wie soll entschieden werden?</p>
              </div>
              <div className="tiebreaker-options">
                <div className="tiebreaker-method">
                  <h4>🖐️ Handwertung</h4>
                  <p>Kampfrichter entscheiden durch Handzeichen</p>
                  <div className="tiebreaker-select">
                    {tieBreakerData.teilnehmer.map(tn => (
                      <button
                        key={tn.wettkaempfer_id}
                        className="tiebreaker-winner-btn"
                        onClick={() => handleTieBreaker('handwertung', tn.wettkaempfer_id)}
                      >
                        {tn.vorname} {tn.nachname}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="tiebreaker-divider">ODER</div>
                <div className="tiebreaker-method">
                  <h4>➕ Extrapunkt</h4>
                  <p>Zusätzliche Wertung entscheidet</p>
                  <div className="tiebreaker-select">
                    {tieBreakerData.teilnehmer.map(tn => (
                      <button
                        key={tn.wettkaempfer_id}
                        className="tiebreaker-winner-btn extra"
                        onClick={() => handleTieBreaker('extrapunkt', tn.wettkaempfer_id)}
                      >
                        {tn.vorname} {tn.nachname}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormenSchreiberDashboard;
